import Vue from 'vue';

/**
 * Küçük bir i18n katmanı. vue-i18n yerine bunu kullanmamın sebebi,
 * uygulamanın geri kalanının hiçbir kurulum değişikliği istememesi:
 * main.js'e tek satır ekleyip her bileşende $t() kullanılabiliyor.
 *
 * Dil sırası: kullanıcının seçtiği (localStorage) > tarayıcı dili > tr
 */

const SUPPORTED = ['tr', 'en', 'fr'];

function detect() {
    try {
        const saved = window.localStorage.getItem('locale');
        if (saved && SUPPORTED.indexOf(saved) > -1) return saved;
    } catch (e) {
        // Gizli sekmede localStorage erişimi hata verebiliyor
    }
    const browser = String(navigator.language || 'tr').slice(0, 2).toLowerCase();
    return SUPPORTED.indexOf(browser) > -1 ? browser : 'tr';
}

// Vue.observable: dil değişince kullanan her bileşen kendiliğinden yeniden
// çiziliyor, elle event yaymaya gerek kalmıyor.
const state = Vue.observable({ locale: detect() });

export function getLocale() {
    return state.locale;
}

export function setLocale(locale) {
    if (SUPPORTED.indexOf(locale) === -1) return;
    state.locale = locale;
    try {
        window.localStorage.setItem('locale', locale);
    } catch (e) { /* yazamıyorsak da dil bu oturum için değişmiş olur */ }
}

export function locales() {
    return SUPPORTED.slice();
}

// 'templates.title' gibi noktalı yolu sözlükte arar.
// Bulamazsa anahtarın kendisini döner — eksik çeviri ekranda görünür kalsın,
// sessizce boş string basmaktan iyi.
function lookup(locale, key) {
    const parts = key.split('.');
    let node = MESSAGES[locale];
    for (let i = 0; i < parts.length; i++) {
        if (!node || typeof node !== 'object') return null;
        node = node[parts[i]];
    }
    return typeof node === 'string' ? node : null;
}

export function t(key, params) {
    let text = lookup(state.locale, key);
    if (text === null) text = lookup('en', key);   // eksikse İngilizceye düş
    if (text === null) return key;

    if (params) {
        Object.keys(params).forEach(function (name) {
            text = text.split('{' + name + '}').join(params[name]);
        });
    }
    return text;
}

// Dizi veya nesne döndürür — özellik kartları, adım listeleri gibi
// yapılar için. t() yalnızca metin döndürüyor.
export function tm(key) {
    const parts = key.split('.');
    function walk(locale) {
        let node = MESSAGES[locale];
        for (let i = 0; i < parts.length; i++) {
            if (!node || typeof node !== 'object') return null;
            node = node[parts[i]];
        }
        return node === undefined ? null : node;
    }
    const found = walk(state.locale);
    return found !== null ? found : (walk('en') || []);
}

// Tarih biçimi de dile bağlı
export function formatDate(value, withTime) {
    if (!value) return '—';
    const map = { tr: 'tr-TR', en: 'en-GB', fr: 'fr-FR' };
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    if (withTime !== false) {
        options.hour = '2-digit';
        options.minute = '2-digit';
    }
    return new Date(value).toLocaleString(map[state.locale] || 'en-GB', options);
}

export default {
    install(VueInstance) {
        VueInstance.mixin({
            computed: {
                $locale() { return state.locale; },
                $locales() { return SUPPORTED; }
            },
            methods: {
                $t: t,
                $tm: tm,
                $setLocale: setLocale,
                $formatDate: formatDate
            }
        });
    }
};


// ---------------------------------------------------------------------------
// Sözlük
// ---------------------------------------------------------------------------

export const MESSAGES = {

    tr: {
        common: {
            save: 'Kaydet', cancel: 'Vazgeç', close: 'Kapat', edit: 'Düzenle',
            delete: 'Sil', create: 'Oluştur', search: 'Ara', loading: 'Yükleniyor…',
            back: 'Geri', next: 'İleri', yes: 'Evet', no: 'Hayır',
            required: 'zorunlu', optional: 'isteğe bağlı',
            page: 'sayfa', of: '/', noRecords: 'Kayıt yok',
            download: 'İndir', preview: 'Önizleme', duplicate: 'Kopyala',
            status: 'Durum', category: 'Kategori', name: 'Ad', email: 'E-posta',
            date: 'Tarih', actions: 'İşlemler', description: 'Açıklama',
            allStatuses: 'Tüm durumlar', allCategories: 'Tüm kategoriler',
            clearFilters: 'Filtreyi temizle',
            emptyFileError: 'Dosya boş geldi. Sayfayı yenileyip tekrar deneyin.',
            downloadFailed: 'Dosya indirilemedi.'
        },
        templates: {
            title: 'Şablonlar',
            contentPlaceholder: '<h1>Hizmet Sözleşmesi</h1>\n<p>{{musteri_ad}} ile ...</p>',
            previewSuffix: '(önizleme)',
            newTemplate: 'Yeni şablon',
            uploadFile: 'Dosya yükle',
            searchPlaceholder: 'Şablon ara',
            colName: 'Ad', colSource: 'Kaynak', colUsage: 'Kullanım',
            colUpdated: 'Güncelleme',
            sourceUpload: 'Yüklendi', sourceBlank: 'Editör',
            statusDraft: 'Taslak', statusActive: 'Aktif', statusArchived: 'Arşiv',
            loadingList: 'Şablonlar yükleniyor…',
            emptyTitle: 'Henüz şablon yok.',
            emptyAction: 'İlk şablonunu oluştur',
            countSuffix: 'şablon',
            editTitle: 'Şablonu düzenle', createTitle: 'Yeni şablon',
            fieldsButton: 'Alanları yerleştir',
            originalFile: 'Orijinal dosyayı indir',
            previewFile: 'PDF önizleme',
            nameLabel: 'Şablon adı',
            namePlaceholder: 'Örn. Hizmet Sözleşmesi',
            nameError: 'Şablon adı en az 3 karakter olmalı.',
            categoryPlaceholder: 'Sözleşme',
            statusHelp: 'Aktif şablonda en az bir imza alanı gerekir.',
            descriptionPlaceholder: 'Bu şablon ne zaman kullanılır?',
            rolesTitle: 'İmza tarafları',
            addRole: 'Taraf ekle',
            rolePlaceholder: 'Taraf adı (Müşteri)',
            roleKeyPlaceholder: 'anahtar',
            roleError: 'Her tarafın adı ve anahtarı dolu olmalı.',
            roleKeyError: 'Taraf anahtarları benzersiz olmalı.',
            actionSign: 'İmzalar', actionApprove: 'Onaylar',
            actionFill: 'Doldurur', actionCc: 'Bilgi alır',
            contentLabel: 'İçerik',
            contentHelp: 'Alan yerleşimi için çift süslü parantez kullan: {sample}',
            contentError: 'İçerik boş olamaz.',
            usedWarning: 'Bu şablon {count} kez kullanılmış. Kaydettiğinde v{version} olarak '
                + 'yeni bir versiyon oluşur; imzalanmış belgeler eski versiyona bağlı kalır.',
            uploadTitle: 'Şablon dosyası yükle',
            uploadPick: 'PDF veya DOCX seç',
            uploadLimit: 'En fazla 20 MB.',
            uploadNamePlaceholder: 'Boş bırakırsan dosya adı kullanılır',
            uploadNoFile: 'Önce bir dosya seç.',
            uploadTooBig: 'Dosya 20 MB sınırını aşıyor.',
            deleteTitle: 'Şablonu sil',
            deleteBody: '{name} listeden kaldırılacak.',
            deleteUsedNote: 'Bu şablonla {count} belge imzalanmış. Kayıt arşivleniyor, '
                + 'silinmiyor — imzalı belgeler erişilebilir kalır.'
        },

        nav: {
            sectionWork: 'Çalışma alanı', sectionAccount: 'Hesap',
            home: 'Ana sayfa', templates: 'Şablonlar', agreements: 'Sözleşmeler',
            verify: 'Doğrulama', profile: 'Profil', organization: 'Kurum',
            logout: 'Çıkış', newEnvelope: 'Yeni gönderim',
            homeSubtitle: 'İmza sürecinin nasıl işlediğini adım adım görün.',
            templatesSubtitle: 'Sık gönderdiğiniz belgeleri şablon olarak saklayın.',
            agreementsSubtitle: 'Gönderdiğiniz belgeleri ve imza durumlarını izleyin.',
            verifySubtitle: 'İmzalı bir belgenin değiştirilmediğini kontrol edin.',
            profileSubtitle: 'Bilgilerinizi ve varsayılan imzanızı yönetin.',
            organizationSubtitle: 'Kurum bilgileri, üyeler ve davetler.',
            newEnvelopeSubtitle: 'Belgeyi seçin, alıcıları girin, gönderin.',
            rateLimitTitle: 'İstek sınırı aşıldı', backToList: 'Listeye dön'
        },
        sign: {
            signerLabel: 'İmzalayan',
            tabUpload: 'Yükle', tabPad: 'İmza tableti',
            tabPadMock: 'İmza tableti (mock)',
            biometricNotice: 'İmzanız tablete atılırken kalem basıncı ve zamanlaması da kaydedilir. Bu veri şifrelenerek saklanır ve yalnızca imzaya itiraz edilmesi hâlinde yetkili inceleme için açılabilir.',

            uploadPick: 'İmza görselini seçin',
            uploadHint: 'PNG veya JPG. Beyaz zemin otomatik olarak saydamlaştırılır.',
            uploadBadType: 'Yalnızca PNG veya JPG yükleyebilirsiniz.',
            uploadTooBig: 'Görsel 5 MB sınırını aşıyor.',
            padConnecting: 'Tablet aranıyor…',
            padReady: 'Tablet hazır. İmzanızı tablete atın.',
            padClear: 'Tableti temizle',
            padEmpty: 'Tablette imza yok. Önce tablete imza atın.',
            padNoScript: 'Tablet desteği bu sunucuda kurulu değil (SigWebTablet.js bulunamadı).',
            padNoService: 'Bu bilgisayarda tablet servisi (SigWeb) çalışmıyor. Kurulu ve tablet bağlı mı?',
            padRetry: 'Tekrar dene',

            guideStart: 'Başla', guideNext: 'Sonraki', guideFinish: 'Bitir',
            guideFill: 'Doldur',
            dragHint: 'Sürükleyerek yerini ayarlayın, tıklayarak değiştirin',
            signHere: 'İmzala', initialHere: 'Paraf',
            adoptTitle: 'İmzanızı oluşturun',
            adoptLead: 'Bu imza belgedeki tüm imza alanlarına uygulanacak.',
            adoptButton: 'Uygula ve imzala',
            fieldsLeft: '{count} alan kaldı',
            allDone: 'Tüm alanlar tamam',
            finishNeedsConsent: 'Göndermeden önce onay kutusunu işaretleyin.',
            finishNeedsFields: 'Önce tüm zorunlu alanları doldurun.',
            changeSignature: 'İmzayı değiştir',

            awaitingTitle: 'İmzanız bekleniyor',
            sentBy: 'Gönderen',
            documentTitle: 'Belge',
            openInNewTab: 'Yeni sekmede aç',
            consentTitle: 'Onay',
            secureNote: 'Bu bağlantı yalnızca size özeldir ve tek kullanımlıktır.',
            doneTitle: 'Tamamlandı',
            footerNote: 'Bu belge {brand} ile imzalandı.',
            footerLink: 'Ürünü inceleyin',

            docUnavailable: 'Belge görüntülenemiyor',
            fieldsTitle: 'Doldurulacak alanlar',
            checkboxLabel: 'Onaylıyorum',
            selectOption: 'Seçin',
            signatureTitle: 'İmzanız',
            tabSaved: 'Kayıtlı imzam', tabDraw: 'Çiz', tabType: 'Yazarak oluştur',
            savedHint: 'Profilinizde kayıtlı imza kullanılacak.',
            drawHint: 'Fareyle veya parmağınızla imzalayın.',
            typePlaceholder: 'Adınızı yazın',
            clear: 'Temizle',
            consent: 'Bu belgeyi elektronik olarak imzalamayı ve elektronik imzamın ıslak imza ile '
                + 'aynı hukuki sonucu doğurmasını kabul ediyorum.',
            submit: 'İmzala ve gönder',
            decline: 'İmzalamayı reddet',
            declineTitle: 'İmzalamayı reddet',
            declineLead: 'Bu işlem geri alınamaz. Belge gönderen tarafa reddedildiği bildirilir.',
            declineReason: 'Gerekçe',
            declineReasonPlaceholder: 'Neden imzalamadığınızı kısaca yazın',
            declineSubmit: 'Reddet',
            blockedHint: 'Bu link üzerinden işlem yapılamıyor. Belgeyi gönderen kişiyle iletişime geçin.',
            doneHint: 'Bu sekmeyi kapatabilirsiniz.',
            missingToken: 'İmza linki eksik veya hatalı.',
            errRequired: 'Doldurulması zorunlu alanlar: {fields}',
            errSignature: 'İmzanızı çizin veya yazarak oluşturun.'
        },
        join: {
            loading: 'Davet kontrol ediliyor…',
            title: 'Ekibe katılın',
            lead: '{org} kurumuna davet edildiniz.',
            signInFirst: 'Daveti kabul etmek için giriş yapın. Hesabınız yoksa giriş ekranından '
                + 'hemen oluşturabilirsiniz.',
            signIn: 'Giriş yap veya kayıt ol',
            signingIn: 'Giriş bekleniyor…',
            accept: 'Daveti kabul et',
            oneOrgOnly: 'Bir kullanıcı yalnızca tek bir kuruma bağlı olabilir.',
            blockedHint: 'Bu davet artık kullanılamıyor. Sizi davet eden kişiyle iletişime geçin.',
            joinedHint: 'Artık kurumun şablonlarına ve imza paketlerine erişebilirsiniz.',
            goToApp: 'Uygulamaya git',
            missingToken: 'Davet linki eksik veya hatalı.'
        },
        verify: {
            title: 'Belge doğrulama',
            expandAll: 'Tümünü aç', collapseAll: 'Tümünü kapat',
            rowAppended: 'İmzadan sonra eklenen bölüm',
            appendedValidation: 'yalnızca doğrulama verisi ({bytes} bayt)',
            appendedNone: 'yok',
            appendedContent: 'içerik değişmiş ({bytes} bayt)',
            appendedHint: 'PDF standardı imzadan sonra doğrulama verisi (DSS/LTV) eklemeye '
                + 'izin veriyor ve Adobe da bunu geçerli sayıyor. Eklenen bölüm sayfa '
                + 'içeriğine dokunmadığı sürece imza bozulmuş olmaz.',

            tsAuthority: 'Damgayı veren',
            tsAuthorityIssuer: 'Onun sertifikasını veren',
            tsSerial: 'Damga seri no',
            tsMatches: 'Damga bu imzaya ait',
            tsMatchesHint: 'Otoritenin kaydettiği özet, belgedeki imza değerinin özetiyle aynı. '
                + 'Tutmasaydı damga başka bir belgeden kopyalanmış olurdu.',
            tsNoAuthority: 'Damga içinde otoritenin sertifikası yok',

            sectionChain: 'Sertifika zinciri',
            chainSigner: 'İmzalayan', chainIntermediate: 'Ara', chainRoot: 'Kök',
            chainIssuer: 'Veren', chainSerial: 'Seri no', chainValidity: 'Geçerlilik',
            chainRevocation: 'İptal kontrolü', chainNoRevocation: 'adres yok',
            chainSelfSigned: 'Kendinden imzalı',
            chainHint: 'Zincir imzalayandan köke doğru sıralı. Her sertifika bir üstteki '
                + 'tarafından verilmiş; kök kendi kendini imzalar.',

            sectionLtv: 'Uzun dönemli doğrulama (LTV)',
            ltvLevel: 'Seviye', ltvNone: 'Yok',
            ltvRevocationEmbedded: 'İptal verisi belgeye gömülü',
            ltvDocTimestamp: 'Belge zaman damgası',
            ltvYes: 'var', ltvNo: 'yok',
            ltvGoodText: 'İptal bilgisi belgenin içinde. Sertifikanın süresi dolsa veya '
                + 'otorite kapansa bile bu imza doğrulanabilir.',
            ltvNoneText: 'İptal bilgisi belgeye gömülü değil. Sertifikanın süresi dolduğunda '
                + 'doğrulayan tarafın sertifika otoritesine bağlanması gerekir; otorite '
                + 'yanıt vermezse imza "doğrulanamadı" görünür.',
            ltvLtText: 'İptal verisi gömülü ama belge zaman damgası yok. Damga eklenirse '
                + 'iptal verisinin hangi anda geçerli olduğu da mühürlenmiş olur.',

            sectionAdobe: 'Adobe Reader ile nasıl kontrol edilir',
            adobeLead: 'Bize hiç bağlanmadan, kendi bilgisayarınızda:',
            adobeStep1: 'Belgeyi Adobe Acrobat Reader ile açın (tarayıcının PDF görüntüleyicisi imzayı göstermez).',
            adobeStep2: 'Sol taraftaki imza panelini açın: "İmza Paneli" düğmesi ya da menüden Görünüm → Göster/Gizle → Gezinme Bölmeleri → İmzalar.',
            adobeStep3: 'İmzanın üzerine tıklayın. "Belge imzalandıktan sonra değiştirilmemiştir" yazıyorsa içerik bozulmamış demektir.',
            adobeStep4: 'İmza Özellikleri düğmesine basın, sonra "İmzalayan Sertifikasını Göster" ile zinciri görün.',
            adobeStep5: 'Sertifika penceresindeki "Güven" sekmesi, imzalayanın kimliğinin doğrulanıp doğrulanmadığını gösterir.',
            adobeStep6: 'Zaman damgası varsa imza özelliklerinde ayrı satırda görünür ve damgayı veren otoritenin adını taşır.',
            adobeNote: 'Sarı üçgen "belge bozuk" demek değildir; imzalayanın kimliğinin '
                + 'bağımsız bir otoriteyle doğrulanamadığını söyler. İçeriğin değişip '
                + 'değişmediği ayrı bir satırda yazar.',

            fileHint: 'En fazla 20 MB.',
            codePlaceholder: 'Örn. A7K2-9MPX-3RTQ',
            rowStatus: 'Durum',
            footer: 'DocuLock',

            subtitle: 'İmzalı bir belgenin gerçekten imzalandığını ve değiştirilmediğini kontrol edin.',
            tabCode: 'Doğrulama kodu ile', tabFile: 'Dosya yükleyerek',
            codeLead: 'İmzalı belgenin son sayfasındaki kodu girin.',
            codeHint: 'Kısa doğrulama kodunu ya da Belge kimliğini (UUID) girebilirsiniz.',
            fileLead: 'PDF dosyasını yükleyin. Dosya sunucuda saklanmaz, yalnızca kontrol edilir.',
            dropHere: 'PDF dosyasını buraya sürükleyin veya seçmek için tıklayın',
            check: 'Kontrol et',
            sectionSignature: 'Dijital imza',
            rowSigned: 'Belge dijital olarak imzalı',
            rowUnmodified: 'İmzalandıktan sonra değiştirilmemiş',
            rowUnmodifiedHint: 'Tek bir karakter değişse bu satır "Hayır" olurdu.',
            rowCovers: 'İmza belgenin tamamını kapsıyor',
            rowCertValid: 'Sertifika geçerlilik süresi içinde',
            rowSigner: 'Mührü atan',
            rowCertPeriod: 'Sertifika geçerliliği',
            selfSignedTitle: 'Mühür sahibi doğrulanamadı.',
            selfSignedText: 'Belge değiştirilmemiş, bunu doğruladık. Ancak mührü atan kuruluşun '
                + 'kimliği bağımsız bir sertifika otoritesi tarafından onaylanmamış. Adobe Reader '
                + 'bu belgeyi açtığında "imzalayanın kimliği bilinmiyor" uyarısı verir.',
            sectionTimestamp: 'Zaman damgası',
            timestampHint: 'Bu zaman bağımsız bir zaman damgası otoritesinden alınmıştır, '
                + 'imzayı atan sistemin saatinden değil.',
            noTimestamp: 'Bu belgede zaman damgası yok. İmza zamanı yalnızca imzalayan sistemin '
                + 'saatine dayanıyor.',
            sectionRecord: 'Kayıt bilgileri',
            rowSubject: 'Konu', rowDocument: 'Belge',
            rowCompleted: 'Tamamlanma', rowCode: 'Doğrulama kodu', rowDocumentId: 'Belge kimliği',
            rowChain: 'Olay zinciri bütün',
            rowChainHint: 'Her kayıt bir öncekinin özetini taşır; aradan kayıt çıkarılsa tutmazdı.',
            sectionSigners: 'İmzalayanlar',
            sectionMatch: 'Kayıtla eşleşme',
            matchYes: 'Bu dosya kayıtlarımızdaki belgeyle birebir aynı.',
            matchNo: 'Bu dosya kayıtlarımızda bulunamadı. Belge başka bir sistemden gelmiş '
                + 'veya üretildikten sonra yeniden kaydedilmiş olabilir.',
            rowChecksum: 'Dosya özeti (SHA-256):',
            sectionSelf: 'Bize sormadan kontrol etmek',
            selfText: 'Belgeyi Adobe Acrobat Reader ile açın. İmza paneli, belgenin imzalandıktan '
                + 'sonra değiştirilip değiştirilmediğini bizim sistemimize hiç bağlanmadan söyler. '
                + 'Dijital imza belgenin içine gömülüdür.',
            notFound: 'Bu koda ait belge bulunamadı.',
            emptyCode: 'Doğrulama kodunu girin.',
            noFile: 'Önce bir PDF dosyası seçin.',
            notPdf: 'Yalnızca PDF dosyası yükleyebilirsiniz.',
            networkError: 'Sunucuya ulaşılamadı. Bağlantınızı kontrol edin.',
            serverError: 'Kontrol sırasında bir hata oluştu.',
            verdictValid: 'Belge doğrulandı',
            verdictValidText: 'İmza geçerli ve belge imzalandıktan sonra değiştirilmemiş.',
            verdictBroken: 'Belge değiştirilmiş',
            verdictBrokenText: 'İmza tutmuyor. Bu dosya imzalandıktan sonra üzerinde değişiklik yapılmış.',
            verdictUnsigned: 'Belge imzalı değil',
            verdictUnsignedText: 'Bu PDF içinde dijital imza bulunamadı.',
            verdictRecord: 'Kayıt bulundu',
            verdictRecordText: 'Belgenin kaydı sistemde mevcut. Dosyanın kendisini de kontrol etmek '
                + 'için "Dosya yükleyerek" sekmesini kullanın.'
        },
        intro: {
            title: 'Nasıl çalışır',
            subtitle: 'Belgeden imzaya, imzadan doğrulamaya kadar yedi adım.',
            stepWord: 'Adım', prev: 'Geri', next: 'İleri', restart: 'Baştan',
            footnote: 'Adımların üzerine tıklayarak istediğiniz aşamaya geçebilirsiniz.',
            steps: [
                { short: 'Belge', title: 'Belgeyi hazırlayın',
                  text: 'İmzalatmak istediğiniz belgeyle başlayın. Elinizde bir PDF varsa yükleyin, '
                      + 'yoksa editörde yazın. Sık kullandığınız belgeleri şablon olarak saklayın.',
                  points: ['PDF veya DOCX yükleyin', 'Ya da sıfırdan yazın', 'Şablonlarınızdan seçin'] },
                { short: 'Alanlar', title: 'Alanları yerleştirin',
                  text: 'İmza, paraf, tarih ve metin alanlarını belgenin üzerinde sürükleyerek '
                      + 'konumlandırın. Her alan bir tarafa bağlanır, o kişi yalnızca kendi alanlarını görür.',
                  points: ['Sürükleyip bırakın', 'Her alanın bir sahibi olur', 'Konumlar oransal tutulur'] },
                { short: 'Alıcılar', title: 'Alıcıları girin',
                  text: 'İmzalayacak kişilerin adını ve e-postasını yazın. Birden fazla kişi varsa '
                      + 'sırayla imzalarlar: ilki tamamlamadan ikinciye davet gitmez.',
                  points: ['Ad ve e-posta yeterli', 'Hesap açmaları gerekmez', 'Sıralı imza desteklenir'] },
                { short: 'Gönderim', title: 'Gönderin',
                  text: 'Her alıcı kendine özel bir bağlantı içeren e-posta alır. Bağlantı tek '
                      + 'kullanımlıktır ve süresi dolduğunda geçersiz olur.',
                  points: ['Kişiye özel bağlantı', 'Tek kullanımlık', 'Hatırlatma gönderebilirsiniz'] },
                { short: 'İmza', title: 'İmzalansın',
                  text: 'Alıcı bağlantıya tıklar, belgeyi tarayıcıda görür, alanları doldurur ve '
                      + 'imzasını çizer. Onay kutusunu işaretlemeden imza tamamlanmaz.',
                  points: ['Kurulum gerekmez', 'Çizerek veya yazarak imza', 'Elektronik işlem onayı alınır'] },
                { short: 'Mühür', title: 'Belge mühürlenir',
                  text: 'Son imzayla birlikte PDF üretilir, sonuna kayıt özeti eklenir ve belge '
                      + 'sertifikayla mühürlenir. Zaman bağımsız bir otoriteden alınır.',
                  points: ['Kriptografik imza gömülür', 'Zaman damgası eklenir', 'Olay zinciri belgeye yazılır'] },
                { short: 'Doğrulama', title: 'Her zaman doğrulanabilir',
                  text: 'Belgeyi eline alan herkes kontrol edebilir. Belgenin üzerindeki kodla '
                      + 'sorgulayabilir, dosyayı yükleyebilir, ya da Adobe Reader ile bize hiç '
                      + 'sormadan bakabilir.',
                  points: ['Doğrulama kodu ile', 'Dosya yükleyerek', 'Adobe Reader ile bağımsız olarak'] }
            ]
        },
        landing: {
            navHow: 'Nasıl çalışır', navVerify: 'Belge doğrula', login: 'Giriş Yap',
            heroTitle: 'Belgelerinizi elektronik olarak imzalatın',
            heroSubtitle: 'Sözleşmeyi yükleyin, imzalayacak kişileri girin, gerisini bize bırakın. '
                + 'Her adım kayıt altına alınır, tamamlanan belge dijital olarak mühürlenir.',
            heroPrimary: 'Başla', heroSecondary: 'Belge doğrula',
            featuresTitle: 'Ne yapıyor',
            featuresLead: 'Islak imza için kimseyi ofise çağırmadan.',
            features: [
                { icon: 'fa-file-text-o', title: 'Şablon veya dosya',
                  text: 'PDF yükleyin, editörde sıfırdan yazın ya da hazır şablonlarınızdan üretin. '
                      + 'Sık kullandığınız belgeleri şablon olarak saklayın.' },
                { icon: 'fa-crosshairs', title: 'Alanları siz yerleştirin',
                  text: 'İmza, paraf, tarih ve metin alanlarını belgenin üzerinde sürükleyerek '
                      + 'konumlandırın. Her alan bir tarafa bağlanır.' },
                { icon: 'fa-envelope-o', title: 'E-posta ile gönderim',
                  text: 'İmzalayanlar kişiye özel bir bağlantı alır. Hesap açmaları gerekmez. '
                      + 'Birden fazla kişi varsa sırayla imzalarlar.' },
                { icon: 'fa-list-alt', title: 'Zincirli denetim izi',
                  text: 'Oluşturma, görüntüleme, imza ve tamamlanma olayları zaman, IP ve tarayıcı '
                      + 'bilgisiyle kayda geçer. Her kayıt bir öncekinin özetini taşır.' },
                { icon: 'fa-lock', title: 'Dijital mühür',
                  text: 'Tamamlanan belge sertifikayla imzalanır. Sonradan tek bir bayt değişirse '
                      + 'imza geçersiz olur ve bu Adobe Reader\'da da görünür.' },
                { icon: 'fa-clock-o', title: 'Zaman damgası',
                  text: 'İmza zamanı bizim sunucumuzun saatine değil, bağımsız bir zaman damgası '
                      + 'otoritesine dayanır.' }
            ],
            howTitle: 'Dört adım',
            steps: [
                { title: 'Belgeyi hazırlayın',
                  text: 'Dosya yükleyin veya şablon seçin, imza alanlarını yerleştirin.' },
                { title: 'Alıcıları girin',
                  text: 'Ad ve e-posta yeterli. Sıralı imza gerekiyorsa sırayı belirleyin.' },
                { title: 'Gönderin',
                  text: 'Herkes kendi bağlantısını alır, tarayıcıdan imzalar.' },
                { title: 'İmzalı belgeyi alın',
                  text: 'Son imzayla birlikte mühürlü PDF ve kayıt özeti hazır olur.' }
            ],
            verifyTitle: 'Elinizdeki belgeyi doğrulayın',
            verifyLead: 'İmzalı belgenin son sayfasındaki doğrulama kodunu girin. '
                + 'Hesabınız olması gerekmiyor.',
            verifyPlaceholder: 'Örn. A7K2-9MPX-3RTQ',
            verifyButton: 'Sorgula',
            verifyHint: 'Belgeyi Adobe Reader ile açarak imzayı bize hiç sormadan da kontrol edebilirsiniz.',
            chainOk: 'Kayıt zinciri geçerli', chainBroken: 'Kayıt zinciri bozuk',
            signedYes: 'Dijital mühürlü', signedNo: 'Mühürsüz',
            timestampLabel: 'Zaman damgası',
            notFound: 'Bu koda ait belge bulunamadı.',
            emptyCode: 'Doğrulama kodunu girin.',
            networkError: 'Sunucuya ulaşılamadı. Bağlantınızı kontrol edin.',
            serverError: 'Sorgulama sırasında bir hata oluştu.',
            footerCredit: 'Tasarım ve Geliştirme DocuLock ekibi tarafından yapılmıştır.',
            footerRights: '2024 © TÜM HAKLARI SAKLIDIR'
        },
        envelopes: {
            title: 'İmza paketleri',
            deleteAction: 'Sil',
            selectAll: 'Sayfadakilerin tümünü seç',
            selectedCount: '{count} seçili',
            clearSelection: 'Seçimi kaldır',
            deleteSelected: 'Seçilenleri sil',
            deleteTitle: '{count} imza paketi silinsin mi?',
            deleteLead: 'Silinen paketler listeden kalkar. Bu işlem geri alınamaz.',
            deleteMore: 've {count} paket daha',
            deleteActiveWarn: '{count} paket hâlâ imza bekliyor. Silinince iptal edilecek ve alıcıların imza bağlantıları kapanacak.',
            deleteKeepNote: 'Tamamlanmış belgeler kanıt olarak saklanır; tarafların elindeki imzalı PDF doğrulanmaya devam eder.',
            deleteConfirm: 'Evet, sil',

            showing: '{from}-{to} / {total}',
            perPage: 'Sayfa başına',
            first: 'İlk sayfa', last: 'Son sayfa',

            searchPlaceholder: 'Konu, ad veya e-posta ile ara',
            viewCards: 'Kart', viewList: 'Liste',
            noMatch: 'Aramanıza uyan kayıt yok.',
            colSigners: 'İmzalayanlar', moreSigners: '+{count}',

            colSubject: 'Konu', colRecipients: 'Alıcılar', colUpdated: 'Güncelleme',
            signCount: '{signed} / {total} imza',
            statusDraft: 'Taslak', statusSent: 'Gönderildi', statusProgress: 'İmza sürüyor',
            statusCompleted: 'Tamamlandı', statusDeclined: 'Reddedildi', statusVoided: 'İptal',
            rcpPending: 'Bekliyor', rcpSent: 'Gönderildi', rcpViewed: 'Görüntüledi',
            rcpSigned: 'İmzaladı', rcpDeclined: 'Reddetti',
            loadingList: 'Paketler yükleniyor…',
            empty: 'Henüz imza paketi yok.',
            countSuffix: 'paket',
            detail: 'Detay', send: 'Gönder', remind: 'Hatırlat',
            downloadOriginal: 'Orijinali indir', downloadSigned: 'İmzalı PDF',
            voidAction: 'İptal et',
            auditValid: 'Denetim izi: geçerli', auditBroken: 'Denetim izi: BOZUK',
            recipientsTitle: 'Alıcılar', eventsTitle: 'Olay zinciri',
            linksTitle: 'İmza linkleri',
            mailSent: 'posta gitti', mailFailed: 'posta gitmedi',
            declineReason: 'Red', sentAt: 'Gönderim',
            voidTitle: 'Paketi iptal et',
            voidBody: '{subject} iptal edilecek. Açık imza linkleri geçersiz olur ve bu geri alınamaz.',
            voidReason: 'Gerekçe',
            evCreated: 'Oluşturuldu', evSent: 'Link üretildi', evMailed: 'Posta gönderildi',
            evViewed: 'Görüntülendi', evSigned: 'İmzalandı', evCompleted: 'Tamamlandı',
            evDeclined: 'Reddedildi', evVoided: 'İptal edildi', evRebuilt: 'PDF yeniden üretildi',
            signedSuffix: '(imzalı)'
        },
        newEnvelope: {
            title: 'Yeni imza paketi',
            stepDocument: 'Belge', stepDetails: 'Detaylar', stepRecipients: 'Alıcılar',
            tabTemplateHint: 'Hazır şablonlarınızdan üretin',
            tabUploadHint: 'PDF veya DOCX dosyası yükleyin',
            tabBlankHint: 'Metni burada yazın',
            order: 'Sıra',

            sent: 'Gönderildi',
            newPackage: 'Yeni paket oluştur',
            document: 'Belge',
            tabTemplate: 'Şablondan', tabUpload: 'Dosya yükle', tabBlank: 'Sıfırdan yaz',
            pickTemplate: 'Şablon seçin',
            groupActive: 'Aktif', groupUnusable: 'Kullanılamaz',
            noTemplates: 'Hiç şablon yok. Şablonlar ekranından bir tane oluşturun, ya da yukarıdan dosya yükleyin.',
            noneActive: '{count} şablon var ama hiçbiri aktif değil. Bir şablonun kullanılabilmesi için '
                + 'Şablonlar ekranında en az bir imza alanı eklenip durumunun "Aktif" yapılması gerekir.',
            noDescription: 'Açıklama yok',
            parties: 'Taraflar',
            uploadHelp: 'En fazla 20 MB. İmza kutusu son sayfanın altına yerleştirilir.',
            saveAsTemplate: 'Bu belgeyi şablon olarak da kaydet',
            templateNamePlaceholder: 'Şablon adı (boş bırakırsan konu kullanılır)',
            subject: 'Konu',
            subjectPlaceholder: 'Alıcının e-postada göreceği başlık',
            message: 'Mesaj',
            messagePlaceholder: 'İsteğe bağlı not',
            recipients: 'Alıcılar',
            addRecipient: 'Alıcı ekle',
            lockedRoles: 'Şablon tarafları belirliyor. Her taraf için kişi girin.',
            orderNote: 'Birden fazla alıcı eklerseniz sırayla imzalarlar: ilki imzalamadan ikinciye link gitmez.',
            createAndSend: 'Oluştur ve gönder',
            saveDraft: 'Taslak olarak kaydet',
            draftSaved: 'Taslak kaydedildi. Göndermek için paketler ekranından ilerleyin. Paket kimliği: {id}',
            linkNote: 'Posta gönderilemediyse bu bağlantıyı elle iletebilirsiniz:',
            errSubject: 'Konu zorunlu.',
            errTemplate: 'Şablon seçin.',
            errFile: 'Dosya seçin.',
            errContent: 'Belge içeriği boş.',
            errRecipient: 'Her alıcının adı ve geçerli e-postası olmalı.',
            errDuplicate: 'Aynı e-posta iki kez eklenmiş.'
        },
        editor: {
            addField: 'Alan ekle',
            zoom: 'Yakınlaştırma', fitWidth: 'Sığdır',
            selectHint: 'Alanı taşımak için sürükleyin, köşesinden boyutlandırın.',
            noFields: 'Henüz alan yok. Soldan tür ve taraf seçip belgeye tıklayın.',
            deleteField: 'Alanı sil',
            pageOf: 'Sayfa {page} / {total}',

            addHelp: 'Tür ve tarafı seçin, sonra belgede istediğiniz yere tıklayın.',
            fieldType: 'Alan türü', party: 'Taraf',
            noRoles: 'Şablonda taraf tanımlı değil. Şablonlar ekranından en az bir taraf ekleyin.',
            ready: 'Aktifleştirmeye hazır: {count} imza alanı var.',
            needSignature: 'Aktifleştirmek için en az bir imza alanı gerekiyor.',
            fields: 'Alanlar',
            label: 'Etiket', requiredField: 'Zorunlu',
            preparing: 'Belge hazırlanıyor…',
            page: 'Sayfa',
            cannotShow: 'Belge görüntülenemedi. Alanları yine de ekleyebilirsiniz ama konumlarını göremezsiniz.',
            saveActivate: 'Kaydet ve aktifleştir',
            toDraft: 'Taslağa al',
            errLabel: 'Etiketi boş bir alan var.',
            errFirstRole: 'Önce şablona en az bir taraf ekleyin.',
            tSignature: 'İmza', tInitials: 'Paraf', tText: 'Metin', tDate: 'Tarih',
            tNumber: 'Sayı', tEmail: 'E-posta', tCheckbox: 'Onay kutusu',
            tDropdown: 'Liste', tTextarea: 'Uzun metin', tStamp: 'Kaşe'
        },
        profile: {
            title: 'Profil',
            account: 'Hesap bilgileri',
            fullName: 'Ad Soyad', jobTitle: 'Unvan', phone: 'Telefon',
            organization: 'Kurum', role: 'Rol', locale: 'Dil / saat dilimi',
            lastLogin: 'Son giriş', timezone: 'Saat dilimi', country: 'Ülke',
            verified: 'doğrulandı', notVerified: 'doğrulanmadı',
            titleHint: 'İmza bloğunda görünür',
            lockedHint: 'E-posta, rol ve kurum bu ekrandan değiştirilemiyor.',
            errName: 'Ad soyad en az 2 karakter olmalı.',
            signatureTitle: 'Varsayılan imzanız',
            signatureLead: 'Kaydederseniz belge imzalarken hazır gelir, her seferinde yeniden çizmeniz gerekmez.',
            change: 'Değiştir',
            tabDraw: 'Çiz', tabType: 'Yazarak oluştur',
            drawHint: 'Fareyle veya parmağınızla imzalayın.',
            typePlaceholder: 'Adınızı yazın',
            saveSignature: 'İmzayı kaydet', clearSignature: 'Temizle',
            errSignature: 'Önce imzanızı çizin veya adınızı yazın.',
            roleOwner: 'Sahip', roleAdmin: 'Yönetici', roleMember: 'Üye', roleViewer: 'Görüntüleyici',
            stActive: 'Aktif', stInvited: 'Davetli', stPassive: 'Pasif', stSuspended: 'Askıda'
        },
        organization: {
            memberCount: '{count} üye',
            copyKey: 'Anahtarı kopyala',
            copyId: 'Kimliği kopyala',
            apiKeyVisible: 'Anahtarlar kurum üyelerine açık görünüyor.',

            alreadyMember: '{email} zaten kurumunuzun üyesi.',
            alreadyInvited: '{email} adresine bekleyen bir davet zaten var.',

            apiTitle: 'API anahtarları',
            apiLead: 'Dış sistemlerin zarf oluşturması için kullanılır.',
            apiNew: 'Yeni anahtar',
            apiName: 'Anahtar adı',
            apiNamePlaceholder: 'Örn. CRM entegrasyonu',
            apiValidity: 'Geçerlilik',
            apiMonths: '{count} ay',
            apiNone: 'Henüz API anahtarı yok.',
            apiCreated: 'Anahtar oluşturuldu',
            apiOnceWarning: 'Bu anahtar bir daha gösterilmeyecek. Şimdi kopyalayıp güvenli bir yere kaydedin.',
            apiAccountId: 'Hesap kimliği',
            apiUsage: 'Kullanım', apiLastUsed: 'Son kullanım', apiNever: 'hiç',
            apiRevoke: 'İptal et',
            apiRevoked: 'İptal edildi',
            apiExpired: 'Süresi doldu',
            apiActive: 'Aktif',
            apiErrName: 'Anahtar adı zorunlu.',
            apiEndpoint: 'Uç adresi',

            invite: 'Kişi davet et',
            info: 'Kurum bilgileri',
            name: 'Kurum adı', legalName: 'Ticari unvan', taxNumber: 'Vergi numarası',
            phone: 'Telefon', address: 'Adres', locale: 'Dil / saat dilimi',
            slugLocked: 'Kısa ad ({slug}) değiştirilemiyor — linklerde kullanılıyor.',
            members: 'Üyeler', person: 'Kişi',
            invitations: 'Davetler', noInvitations: 'Bekleyen davet yok.',
            inviteLanguage: 'Dil', validity: 'Geçerlilik',
            inviteTitle: 'Kişi davet et',
            inviteLead: 'Davet edilen kişi e-postasındaki bağlantıyla kuruma katılır.',
            copyLink: 'Bağlantıyı kopyala',
            copied: 'Kopyalandı',
            inviteSentTitle: 'Davet hazır',

            inviteName: 'Ad Soyad', inviteEmail: 'E-posta',
            inviteLangHelp: 'Davet e-postası bu dilde gönderilir.',
            inviteRole: 'Rol',
            inviteCreated: '{name} için davet oluşturuldu.',
            inviteLinkHint: 'Posta gönderilemediyse bu bağlantıyı elle iletebilirsiniz:',
            sendInvite: 'Davet gönder',
            mailNotSent: 'posta gitmedi',
            invPending: 'Bekliyor', invAccepted: 'Kabul edildi',
            invRevoked: 'İptal', invExpired: 'Süresi doldu',
            errName: 'Kurum adı zorunlu.',
            errInviteName: 'Ad zorunlu.',
            errInviteEmail: 'Geçerli bir e-posta girin.',
            notInOrg: 'Henüz bir kuruma bağlı değilsiniz.'
        },
        orgSetup: {
            title: 'Kurumunuzu oluşturun',
            lead: 'Şablonlarınız, imza paketleriniz ve ekip arkadaşlarınız bir kuruma bağlı '
                + 'tutulur. Devam etmek için önce kurumunuzu tanımlayın.',
            nameLabel: 'Kurum adı',
            namePlaceholder: 'Örn. DocuLock Yazılım Ltd. Şti.',
            nameError: 'Kurum adı zorunlu.',
            slugPreview: 'Kısa ad',
            taxNumber: 'Vergi numarası',
            phone: 'Telefon',
            address: 'Adres',
            help: 'Zorunlu alan yalnızca kurum adı. Diğerlerini sonradan ayarlardan '
                + 'doldurabilirsiniz.',
            submit: 'Oluştur ve devam et'
        }
    },

    en: {
        common: {
            save: 'Save', cancel: 'Cancel', close: 'Close', edit: 'Edit',
            delete: 'Delete', create: 'Create', search: 'Search', loading: 'Loading…',
            back: 'Back', next: 'Next', yes: 'Yes', no: 'No',
            required: 'required', optional: 'optional',
            page: 'page', of: 'of', noRecords: 'No records',
            download: 'Download', preview: 'Preview', duplicate: 'Duplicate',
            status: 'Status', category: 'Category', name: 'Name', email: 'E-mail',
            date: 'Date', actions: 'Actions', description: 'Description',
            allStatuses: 'All statuses', allCategories: 'All categories',
            clearFilters: 'Clear filters',
            emptyFileError: 'The file came back empty. Refresh the page and try again.',
            downloadFailed: 'Could not download the file.'
        },
        templates: {
            title: 'Templates',
            contentPlaceholder: '<h1>Service Agreement</h1>\n<p>Between {{customer_name}} and ...</p>',
            previewSuffix: '(preview)',
            newTemplate: 'New template',
            uploadFile: 'Upload a file',
            searchPlaceholder: 'Search templates',
            colName: 'Name', colSource: 'Source', colUsage: 'Used',
            colUpdated: 'Updated',
            sourceUpload: 'Uploaded', sourceBlank: 'Editor',
            statusDraft: 'Draft', statusActive: 'Active', statusArchived: 'Archived',
            loadingList: 'Loading templates…',
            emptyTitle: 'No templates yet.',
            emptyAction: 'Create your first template',
            countSuffix: 'templates',
            editTitle: 'Edit template', createTitle: 'New template',
            fieldsButton: 'Place fields',
            originalFile: 'Download original file',
            previewFile: 'PDF preview',
            nameLabel: 'Template name',
            namePlaceholder: 'e.g. Service Agreement',
            nameError: 'Template name must be at least 3 characters.',
            categoryPlaceholder: 'Contract',
            statusHelp: 'An active template needs at least one signature field.',
            descriptionPlaceholder: 'When is this template used?',
            rolesTitle: 'Signing parties',
            addRole: 'Add party',
            rolePlaceholder: 'Party name (Customer)',
            roleKeyPlaceholder: 'key',
            roleError: 'Every party needs a name and a key.',
            roleKeyError: 'Party keys must be unique.',
            actionSign: 'Signs', actionApprove: 'Approves',
            actionFill: 'Fills in', actionCc: 'Receives a copy',
            contentLabel: 'Content',
            contentHelp: 'Use double curly braces to place fields: {sample}',
            contentError: 'Content cannot be empty.',
            usedWarning: 'This template has been used {count} times. Saving creates version '
                + 'v{version}; signed documents stay bound to the earlier version.',
            uploadTitle: 'Upload a template file',
            uploadPick: 'Choose a PDF or DOCX',
            uploadLimit: 'Up to 20 MB.',
            uploadNamePlaceholder: 'Leave blank to use the file name',
            uploadNoFile: 'Choose a file first.',
            uploadTooBig: 'The file exceeds the 20 MB limit.',
            deleteTitle: 'Delete template',
            deleteBody: '{name} will be removed from the list.',
            deleteUsedNote: '{count} documents were signed with this template. The record is '
                + 'archived rather than deleted — signed documents stay accessible.'
        },

        nav: {
            sectionWork: 'Workspace', sectionAccount: 'Account',
            home: 'Home', templates: 'Templates', agreements: 'Agreements',
            verify: 'Verification', profile: 'Profile', organization: 'Organisation',
            logout: 'Sign out', newEnvelope: 'New envelope',
            homeSubtitle: 'See how the signing process works, step by step.',
            templatesSubtitle: 'Keep the documents you send often as templates.',
            agreementsSubtitle: 'Track what you sent and where each signature stands.',
            verifySubtitle: 'Check that a signed document has not been altered.',
            profileSubtitle: 'Manage your details and your default signature.',
            organizationSubtitle: 'Organisation details, members and invitations.',
            newEnvelopeSubtitle: 'Pick the document, add recipients, send.',
            rateLimitTitle: 'Rate limit reached', backToList: 'Back to the list'
        },
        sign: {
            signerLabel: 'Signer',
            tabUpload: 'Upload', tabPad: 'Signature pad',
            tabPadMock: 'Signature pad (mock)',
            biometricNotice: 'While you sign on the pad, pen pressure and timing are recorded as well. This data is stored encrypted and can only be opened for an authorised examination if the signature is disputed.',

            uploadPick: 'Choose a signature image',
            uploadHint: 'PNG or JPG. A white background is made transparent automatically.',
            uploadBadType: 'Only PNG or JPG images can be uploaded.',
            uploadTooBig: 'The image exceeds the 5 MB limit.',
            padConnecting: 'Looking for the pad…',
            padReady: 'Pad ready. Sign on the pad.',
            padClear: 'Clear the pad',
            padEmpty: 'Nothing on the pad yet. Sign on the pad first.',
            padNoScript: 'Signature pad support is not installed on this server (SigWebTablet.js missing).',
            padNoService: 'The pad service (SigWeb) is not running on this computer. Is it installed and the pad connected?',
            padRetry: 'Try again',

            guideStart: 'Start', guideNext: 'Next', guideFinish: 'Finish',
            guideFill: 'Fill in',
            dragHint: 'Drag to adjust the position, click to change',
            signHere: 'Sign', initialHere: 'Initial',
            adoptTitle: 'Create your signature',
            adoptLead: 'This signature will be applied to every signature field in the document.',
            adoptButton: 'Adopt and sign',
            fieldsLeft: '{count} fields left',
            allDone: 'All fields complete',
            finishNeedsConsent: 'Tick the consent box before submitting.',
            finishNeedsFields: 'Fill in all required fields first.',
            changeSignature: 'Change signature',

            awaitingTitle: 'Your signature is requested',
            sentBy: 'Sent by',
            documentTitle: 'Document',
            openInNewTab: 'Open in a new tab',
            consentTitle: 'Consent',
            secureNote: 'This link is personal to you and can be used once.',
            doneTitle: 'Done',
            footerNote: 'This document was signed with {brand}.',
            footerLink: 'See the product',

            docUnavailable: 'The document cannot be displayed',
            fieldsTitle: 'Fields to fill in',
            checkboxLabel: 'I agree',
            selectOption: 'Choose',
            signatureTitle: 'Your signature',
            tabSaved: 'My saved signature', tabDraw: 'Draw', tabType: 'Type it',
            savedHint: 'The signature saved in your profile will be used.',
            drawHint: 'Sign with your mouse or finger.',
            typePlaceholder: 'Type your name',
            clear: 'Clear',
            consent: 'I agree to sign this document electronically and that my electronic '
                + 'signature carries the same legal effect as a handwritten one.',
            submit: 'Sign and submit',
            decline: 'Decline to sign',
            declineTitle: 'Decline to sign',
            declineLead: 'This cannot be undone. The sender will be told that you declined.',
            declineReason: 'Reason',
            declineReasonPlaceholder: 'Briefly say why you are not signing',
            declineSubmit: 'Decline',
            blockedHint: 'This link can no longer be used. Contact the person who sent the document.',
            doneHint: 'You can close this tab.',
            missingToken: 'The signing link is missing or malformed.',
            errRequired: 'Required fields: {fields}',
            errSignature: 'Draw your signature or create one by typing.'
        },
        join: {
            loading: 'Checking the invitation…',
            title: 'Join the team',
            lead: 'You have been invited to join {org}.',
            signInFirst: 'Sign in to accept the invitation. If you do not have an account yet, '
                + 'you can create one from the sign-in screen.',
            signIn: 'Sign in or create an account',
            signingIn: 'Waiting for sign-in…',
            accept: 'Accept invitation',
            oneOrgOnly: 'A user can belong to only one organisation.',
            blockedHint: 'This invitation can no longer be used. Contact the person who invited you.',
            joinedHint: 'You now have access to the organisation\'s templates and signature packages.',
            goToApp: 'Go to the app',
            missingToken: 'The invitation link is missing or malformed.'
        },
        verify: {
            title: 'Verify a document',
            expandAll: 'Expand all', collapseAll: 'Collapse all',
            rowAppended: 'Appended after signing',
            appendedValidation: 'validation data only ({bytes} bytes)',
            appendedNone: 'nothing',
            appendedContent: 'content was changed ({bytes} bytes)',
            appendedHint: 'The PDF standard allows validation data (DSS/LTV) to be appended '
                + 'after signing, and Adobe treats it as valid. As long as the appended part '
                + 'does not touch page content, the signature is not broken.',

            tsAuthority: 'Timestamp issued by',
            tsAuthorityIssuer: 'Their certificate issued by',
            tsSerial: 'Timestamp serial',
            tsMatches: 'The timestamp belongs to this signature',
            tsMatchesHint: 'The digest recorded by the authority matches the digest of the '
                + 'signature in this file. If it did not, the timestamp would have been '
                + 'copied from another document.',
            tsNoAuthority: 'The token does not carry the authority certificate',

            sectionChain: 'Certificate chain',
            chainSigner: 'Signer', chainIntermediate: 'Intermediate', chainRoot: 'Root',
            chainIssuer: 'Issued by', chainSerial: 'Serial', chainValidity: 'Valid',
            chainRevocation: 'Revocation check', chainNoRevocation: 'no address',
            chainSelfSigned: 'Self-signed',
            chainHint: 'The chain runs from the signer up to the root. Each certificate is '
                + 'issued by the one above it; the root signs itself.',

            sectionLtv: 'Long-term validation (LTV)',
            ltvLevel: 'Level', ltvNone: 'None',
            ltvRevocationEmbedded: 'Revocation data embedded',
            ltvDocTimestamp: 'Document timestamp',
            ltvYes: 'yes', ltvNo: 'no',
            ltvGoodText: 'The revocation data sits inside the document. This signature stays '
                + 'verifiable even after the certificate expires or the authority shuts down.',
            ltvNoneText: 'No revocation data is embedded. Once the certificate expires, the '
                + 'verifier has to reach the certificate authority; if it does not answer, '
                + 'the signature shows as "could not be verified".',
            ltvLtText: 'Revocation data is embedded but there is no document timestamp. Adding '
                + 'one also seals the moment at which that data was valid.',

            sectionAdobe: 'How to check this in Adobe Reader',
            adobeLead: 'On your own computer, without contacting us at all:',
            adobeStep1: 'Open the document in Adobe Acrobat Reader (a browser PDF viewer will not show the signature).',
            adobeStep2: 'Open the signature panel on the left: the "Signature Panel" button, or View → Show/Hide → Navigation Panes → Signatures.',
            adobeStep3: 'Click the signature. If it says the document has not been modified since signing, the content is intact.',
            adobeStep4: 'Press Signature Properties, then "Show Signer\'s Certificate" to see the chain.',
            adobeStep5: 'The Trust tab in the certificate window tells you whether the signer\'s identity was verified.',
            adobeStep6: 'If a timestamp is present it appears on its own line in the signature properties, naming the authority that issued it.',
            adobeNote: 'A yellow triangle does not mean the document is broken; it means the '
                + 'signer\'s identity could not be confirmed by an independent authority. '
                + 'Whether the content changed is stated on a separate line.',

            fileHint: 'Up to 20 MB.',
            codePlaceholder: 'e.g. A7K2-9MPX-3RTQ',
            rowStatus: 'Status',
            footer: 'DocuLock',

            subtitle: 'Check that a signed document was really signed and has not been altered.',
            tabCode: 'By verification code', tabFile: 'By uploading the file',
            codeLead: 'Enter the code printed on the last page of the signed document.',
            codeHint: 'You can enter either the short verification code or the Document ID (UUID).',
            fileLead: 'Upload the PDF. The file is only checked, never stored.',
            dropHere: 'Drop the PDF here, or click to choose',
            check: 'Check',
            sectionSignature: 'Digital signature',
            rowSigned: 'Document is digitally signed',
            rowUnmodified: 'Not modified after signing',
            rowUnmodifiedHint: 'A single changed character would turn this into "No".',
            rowCovers: 'Signature covers the whole document',
            rowCertValid: 'Certificate within its validity period',
            rowSigner: 'Sealed by',
            rowCertPeriod: 'Certificate validity',
            selfSignedTitle: 'The sealing party could not be verified.',
            selfSignedText: 'The document has not been altered — we verified that. But the identity '
                + 'of the organisation that sealed it has not been confirmed by an independent '
                + 'certificate authority. Adobe Reader will show "signer identity unknown".',
            sectionTimestamp: 'Timestamp',
            timestampHint: 'This time comes from an independent timestamp authority, '
                + 'not from the clock of the signing system.',
            noTimestamp: 'This document has no timestamp. The signing time rests only on the '
                + 'signing system\'s own clock.',
            sectionRecord: 'Record',
            rowSubject: 'Subject', rowDocument: 'Document',
            rowCompleted: 'Completed', rowCode: 'Verification code', rowDocumentId: 'Document ID',
            rowChain: 'Event chain intact',
            rowChainHint: 'Each entry carries the hash of the previous one; removing one would break it.',
            sectionSigners: 'Signers',
            sectionMatch: 'Match against our record',
            matchYes: 'This file is byte-for-byte the document in our records.',
            matchNo: 'This file was not found in our records. It may come from another system, '
                + 'or have been re-saved after it was produced.',
            rowChecksum: 'File digest (SHA-256):',
            sectionSelf: 'Checking without asking us',
            selfText: 'Open the document in Adobe Acrobat Reader. The signature panel tells you '
                + 'whether it was altered after signing, without ever contacting our system. '
                + 'The digital signature is embedded in the file itself.',
            notFound: 'No document found for this code.',
            emptyCode: 'Enter the verification code.',
            noFile: 'Choose a PDF file first.',
            notPdf: 'Only PDF files can be checked.',
            networkError: 'Could not reach the server. Check your connection.',
            serverError: 'Something went wrong while checking.',
            verdictValid: 'Document verified',
            verdictValidText: 'The signature is valid and the document has not been altered since signing.',
            verdictBroken: 'Document has been altered',
            verdictBrokenText: 'The signature does not match. This file was changed after it was signed.',
            verdictUnsigned: 'Document is not signed',
            verdictUnsignedText: 'No digital signature was found inside this PDF.',
            verdictRecord: 'Record found',
            verdictRecordText: 'The document exists in our records. To check the file itself, '
                + 'use the "By uploading the file" tab.'
        },
        intro: {
            title: 'How it works',
            subtitle: 'Seven steps, from document to signature to verification.',
            stepWord: 'Step', prev: 'Back', next: 'Next', restart: 'Start over',
            footnote: 'Click any step above to jump straight to it.',
            steps: [
                { short: 'Document', title: 'Prepare the document',
                  text: 'Start with whatever you need signed. Upload a PDF if you have one, or '
                      + 'write it in the editor. Keep the documents you send often as templates.',
                  points: ['Upload a PDF or DOCX', 'Or write one from scratch', 'Pick from your templates'] },
                { short: 'Fields', title: 'Place the fields',
                  text: 'Drag signature, initial, date and text fields onto the document. Each '
                      + 'field belongs to one party, and that person only sees their own.',
                  points: ['Drag and drop', 'Every field has an owner', 'Positions are stored proportionally'] },
                { short: 'Recipients', title: 'Add the recipients',
                  text: 'Enter the name and e-mail of everyone who needs to sign. With several '
                      + 'people they sign in order: the second invitation waits for the first signature.',
                  points: ['Name and e-mail is enough', 'No account needed', 'Sequential signing supported'] },
                { short: 'Delivery', title: 'Send it',
                  text: 'Each recipient gets an e-mail with a link of their own. The link is '
                      + 'single-use and stops working once it expires.',
                  points: ['A personal link each', 'Single use', 'Reminders can be sent'] },
                { short: 'Signing', title: 'They sign',
                  text: 'The recipient opens the link, reads the document in the browser, fills '
                      + 'the fields and draws a signature. Nothing is recorded until they tick the '
                      + 'consent box.',
                  points: ['Nothing to install', 'Draw or type the signature', 'Consent is captured'] },
                { short: 'Seal', title: 'The document is sealed',
                  text: 'With the last signature the PDF is produced, a record summary is appended, '
                      + 'and the file is sealed with a certificate. The time comes from an '
                      + 'independent authority.',
                  points: ['Cryptographic signature embedded', 'Timestamp applied', 'Event chain written into the file'] },
                { short: 'Verify', title: 'Verifiable forever after',
                  text: 'Anyone holding the document can check it — with the code printed on it, '
                      + 'by uploading the file, or in Adobe Reader without contacting us at all.',
                  points: ['By verification code', 'By uploading the file', 'Independently in Adobe Reader'] }
            ]
        },
        landing: {
            navHow: 'How it works', navVerify: 'Verify a document', login: 'Login',
            heroTitle: 'Get your documents signed electronically',
            heroSubtitle: 'Upload the contract, add the people who need to sign, and let us handle '
                + 'the rest. Every step is recorded, and the finished document is digitally sealed.',
            heroPrimary: 'Get started', heroSecondary: 'Verify a document',
            featuresTitle: 'What it does',
            featuresLead: 'Without calling anyone into the office for a wet signature.',
            features: [
                { icon: 'fa-file-text-o', title: 'Template or file',
                  text: 'Upload a PDF, write one from scratch in the editor, or start from a saved '
                      + 'template. Keep the documents you send often.' },
                { icon: 'fa-crosshairs', title: 'Place the fields yourself',
                  text: 'Drag signature, initial, date and text fields onto the document. '
                      + 'Each field belongs to one party.' },
                { icon: 'fa-envelope-o', title: 'Sent by e-mail',
                  text: 'Signers get a personal link. No account needed. '
                      + 'With several signers, they sign in order.' },
                { icon: 'fa-list-alt', title: 'Chained audit trail',
                  text: 'Creation, viewing, signing and completion are recorded with time, IP and '
                      + 'browser. Each entry carries the hash of the one before it.' },
                { icon: 'fa-lock', title: 'Digital seal',
                  text: 'The finished document is signed with a certificate. Change a single byte '
                      + 'afterwards and the signature breaks — visible in Adobe Reader too.' },
                { icon: 'fa-clock-o', title: 'Timestamp',
                  text: 'The signing time comes from an independent timestamp authority, not from '
                      + 'our own clock.' }
            ],
            howTitle: 'Four steps',
            steps: [
                { title: 'Prepare the document',
                  text: 'Upload a file or pick a template, then place the signature fields.' },
                { title: 'Add recipients',
                  text: 'Name and e-mail is enough. Set the order if signing must be sequential.' },
                { title: 'Send',
                  text: 'Everyone gets their own link and signs in the browser.' },
                { title: 'Get the signed file',
                  text: 'The sealed PDF and the record summary are ready with the last signature.' }
            ],
            verifyTitle: 'Verify a document you received',
            verifyLead: 'Enter the verification code printed on the last page of the signed '
                + 'document. No account required.',
            verifyPlaceholder: 'e.g. A7K2-9MPX-3RTQ',
            verifyButton: 'Check',
            verifyHint: 'You can also open the file in Adobe Reader and check the signature without '
                + 'asking us at all.',
            chainOk: 'Record chain valid', chainBroken: 'Record chain broken',
            signedYes: 'Digitally sealed', signedNo: 'Not sealed',
            timestampLabel: 'Timestamp',
            notFound: 'No document found for this code.',
            emptyCode: 'Enter the verification code.',
            networkError: 'Could not reach the server. Check your connection.',
            serverError: 'Something went wrong while checking.',
            footerCredit: 'Designed and developed by the DocuLock team.',
            footerRights: '2024 © ALL RIGHTS RESERVED'
        },
        envelopes: {
            title: 'Signature packages',
            deleteAction: 'Delete',
            selectAll: 'Select everything on this page',
            selectedCount: '{count} selected',
            clearSelection: 'Clear selection',
            deleteSelected: 'Delete selected',
            deleteTitle: 'Delete {count} signature packages?',
            deleteLead: 'Deleted packages disappear from the list. This cannot be undone.',
            deleteMore: 'and {count} more',
            deleteActiveWarn: '{count} packages are still waiting for signatures. They will be voided and their signing links closed.',
            deleteKeepNote: 'Completed documents are kept as evidence; the signed PDF the parties hold still verifies.',
            deleteConfirm: 'Yes, delete',

            showing: '{from}-{to} of {total}',
            perPage: 'Per page',
            first: 'First page', last: 'Last page',

            searchPlaceholder: 'Search by subject, name or e-mail',
            viewCards: 'Cards', viewList: 'List',
            noMatch: 'Nothing matches your search.',
            colSigners: 'Signers', moreSigners: '+{count}',

            colSubject: 'Subject', colRecipients: 'Recipients', colUpdated: 'Updated',
            signCount: '{signed} / {total} signed',
            statusDraft: 'Draft', statusSent: 'Sent', statusProgress: 'In progress',
            statusCompleted: 'Completed', statusDeclined: 'Declined', statusVoided: 'Voided',
            rcpPending: 'Pending', rcpSent: 'Sent', rcpViewed: 'Viewed',
            rcpSigned: 'Signed', rcpDeclined: 'Declined',
            loadingList: 'Loading packages…',
            empty: 'No signature packages yet.',
            countSuffix: 'packages',
            detail: 'Details', send: 'Send', remind: 'Remind',
            downloadOriginal: 'Download original', downloadSigned: 'Signed PDF',
            voidAction: 'Void',
            auditValid: 'Audit trail: valid', auditBroken: 'Audit trail: BROKEN',
            recipientsTitle: 'Recipients', eventsTitle: 'Event chain',
            linksTitle: 'Signing links',
            mailSent: 'e-mail sent', mailFailed: 'e-mail failed',
            declineReason: 'Declined', sentAt: 'Sent',
            voidTitle: 'Void package',
            voidBody: '{subject} will be voided. Open signing links stop working and this cannot be undone.',
            voidReason: 'Reason',
            evCreated: 'Created', evSent: 'Link issued', evMailed: 'E-mail sent',
            evViewed: 'Viewed', evSigned: 'Signed', evCompleted: 'Completed',
            evDeclined: 'Declined', evVoided: 'Voided', evRebuilt: 'Output regenerated',
            signedSuffix: '(signed)'
        },
        newEnvelope: {
            title: 'New signature package',
            stepDocument: 'Document', stepDetails: 'Details', stepRecipients: 'Recipients',
            tabTemplateHint: 'Start from one of your templates',
            tabUploadHint: 'Upload a PDF or DOCX file',
            tabBlankHint: 'Write the text here',
            order: 'Order',

            sent: 'Sent',
            newPackage: 'Create another package',
            document: 'Document',
            tabTemplate: 'From a template', tabUpload: 'Upload a file', tabBlank: 'Write from scratch',
            pickTemplate: 'Choose a template',
            groupActive: 'Active', groupUnusable: 'Unusable',
            noTemplates: 'No templates yet. Create one from the Templates screen, or upload a file above.',
            noneActive: 'There are {count} templates but none are active. To use a template, add at '
                + 'least one signature field on the Templates screen and set its status to "Active".',
            noDescription: 'No description',
            parties: 'Parties',
            uploadHelp: 'Up to 20 MB. The signature box is placed at the bottom of the last page.',
            saveAsTemplate: 'Also save this document as a template',
            templateNamePlaceholder: 'Template name (leave blank to use the subject)',
            subject: 'Subject',
            subjectPlaceholder: 'The heading the recipient sees in the e-mail',
            message: 'Message',
            messagePlaceholder: 'Optional note',
            recipients: 'Recipients',
            addRecipient: 'Add recipient',
            lockedRoles: 'The template defines the parties. Enter a person for each one.',
            orderNote: 'With several recipients they sign in order: the second link waits for the first signature.',
            createAndSend: 'Create and send',
            saveDraft: 'Save as draft',
            draftSaved: 'Draft saved. Continue from the packages screen to send it. Package ID: {id}',
            linkNote: 'If the e-mail could not be sent, you can pass this link on yourself:',
            errSubject: 'Subject is required.',
            errTemplate: 'Choose a template.',
            errFile: 'Choose a file.',
            errContent: 'The document content is empty.',
            errRecipient: 'Every recipient needs a name and a valid e-mail.',
            errDuplicate: 'The same e-mail was added twice.'
        },
        editor: {
            addField: 'Add a field',
            zoom: 'Zoom', fitWidth: 'Fit',
            selectHint: 'Drag a field to move it, use the corner to resize.',
            noFields: 'No fields yet. Pick a type and a party on the left, then click the document.',
            deleteField: 'Delete field',
            pageOf: 'Page {page} of {total}',

            addHelp: 'Pick a type and a party, then click where you want it on the document.',
            fieldType: 'Field type', party: 'Party',
            noRoles: 'No parties defined on this template. Add at least one from the Templates screen.',
            ready: 'Ready to activate: {count} signature fields.',
            needSignature: 'At least one signature field is needed to activate.',
            fields: 'Fields',
            label: 'Label', requiredField: 'Required',
            preparing: 'Preparing the document…',
            page: 'Page',
            cannotShow: 'The document could not be displayed. You can still add fields, but not see where they land.',
            saveActivate: 'Save and activate',
            toDraft: 'Move to draft',
            errLabel: 'One of the fields has an empty label.',
            errFirstRole: 'Add at least one party to the template first.',
            tSignature: 'Signature', tInitials: 'Initials', tText: 'Text', tDate: 'Date',
            tNumber: 'Number', tEmail: 'E-mail', tCheckbox: 'Checkbox',
            tDropdown: 'Dropdown', tTextarea: 'Long text', tStamp: 'Stamp'
        },
        profile: {
            title: 'Profile',
            account: 'Account details',
            fullName: 'Full name', jobTitle: 'Job title', phone: 'Phone',
            organization: 'Organisation', role: 'Role', locale: 'Language / time zone',
            lastLogin: 'Last sign-in', timezone: 'Time zone', country: 'Country',
            verified: 'verified', notVerified: 'not verified',
            titleHint: 'Appears in the signature block',
            lockedHint: 'E-mail, role and organisation cannot be changed here.',
            errName: 'Full name must be at least 2 characters.',
            signatureTitle: 'Your default signature',
            signatureLead: 'Save it once and it is ready whenever you sign, instead of drawing it every time.',
            change: 'Change',
            tabDraw: 'Draw', tabType: 'Type it',
            drawHint: 'Sign with your mouse or finger.',
            typePlaceholder: 'Type your name',
            saveSignature: 'Save signature', clearSignature: 'Clear',
            errSignature: 'Draw your signature or type your name first.',
            roleOwner: 'Owner', roleAdmin: 'Admin', roleMember: 'Member', roleViewer: 'Viewer',
            stActive: 'Active', stInvited: 'Invited', stPassive: 'Inactive', stSuspended: 'Suspended'
        },
        organization: {
            memberCount: '{count} members',
            copyKey: 'Copy key',
            copyId: 'Copy ID',
            apiKeyVisible: 'Keys are visible to everyone in the organisation.',

            alreadyMember: '{email} is already a member of your organisation.',
            alreadyInvited: 'There is already a pending invitation for {email}.',

            apiTitle: 'API keys',
            apiLead: 'Used by external systems to create envelopes.',
            apiNew: 'New key',
            apiName: 'Key name',
            apiNamePlaceholder: 'e.g. CRM integration',
            apiValidity: 'Validity',
            apiMonths: '{count} months',
            apiNone: 'No API keys yet.',
            apiCreated: 'Key created',
            apiOnceWarning: 'This key will not be shown again. Copy it now and store it somewhere safe.',
            apiAccountId: 'Account ID',
            apiUsage: 'Calls', apiLastUsed: 'Last used', apiNever: 'never',
            apiRevoke: 'Revoke',
            apiRevoked: 'Revoked',
            apiExpired: 'Expired',
            apiActive: 'Active',
            apiErrName: 'A key name is required.',
            apiEndpoint: 'Endpoint',

            invite: 'Invite someone',
            info: 'Organisation details',
            name: 'Organisation name', legalName: 'Legal name', taxNumber: 'Tax number',
            phone: 'Phone', address: 'Address', locale: 'Language / time zone',
            slugLocked: 'The short name ({slug}) cannot be changed — it is used in links.',
            members: 'Members', person: 'Person',
            invitations: 'Invitations', noInvitations: 'No pending invitations.',
            inviteLanguage: 'Language', validity: 'Valid until',
            inviteTitle: 'Invite someone',
            inviteLead: 'The person you invite joins the organisation through the link in their e-mail.',
            copyLink: 'Copy link',
            copied: 'Copied',
            inviteSentTitle: 'Invitation ready',

            inviteName: 'Full name', inviteEmail: 'E-mail',
            inviteLangHelp: 'The invitation e-mail is sent in this language.',
            inviteRole: 'Role',
            inviteCreated: 'Invitation created for {name}.',
            inviteLinkHint: 'If the e-mail could not be sent, you can pass this link on yourself:',
            sendInvite: 'Send invitation',
            mailNotSent: 'e-mail failed',
            invPending: 'Pending', invAccepted: 'Accepted',
            invRevoked: 'Revoked', invExpired: 'Expired',
            errName: 'Organisation name is required.',
            errInviteName: 'Name is required.',
            errInviteEmail: 'Enter a valid e-mail address.',
            notInOrg: 'You do not belong to an organisation yet.'
        },
        orgSetup: {
            title: 'Create your organisation',
            lead: 'Your templates, signature packages and teammates all belong to an '
                + 'organisation. Set yours up to continue.',
            nameLabel: 'Organisation name',
            namePlaceholder: 'e.g. DocuLock Software Ltd.',
            nameError: 'Organisation name is required.',
            slugPreview: 'Short name',
            taxNumber: 'Tax number',
            phone: 'Phone',
            address: 'Address',
            help: 'Only the organisation name is required. You can fill in the rest later '
                + 'from settings.',
            submit: 'Create and continue'
        }
    },

    fr: {
        common: {
            save: 'Enregistrer', cancel: 'Annuler', close: 'Fermer', edit: 'Modifier',
            delete: 'Supprimer', create: 'Créer', search: 'Rechercher', loading: 'Chargement…',
            back: 'Retour', next: 'Suivant', yes: 'Oui', no: 'Non',
            required: 'obligatoire', optional: 'facultatif',
            page: 'page', of: 'sur', noRecords: 'Aucun enregistrement',
            download: 'Télécharger', preview: 'Aperçu', duplicate: 'Dupliquer',
            status: 'Statut', category: 'Catégorie', name: 'Nom', email: 'E-mail',
            date: 'Date', actions: 'Actions', description: 'Description',
            allStatuses: 'Tous les statuts', allCategories: 'Toutes les catégories',
            clearFilters: 'Effacer les filtres',
            emptyFileError: 'Le fichier est arrivé vide. Actualisez la page et réessayez.',
            downloadFailed: 'Impossible de télécharger le fichier.'
        },
        templates: {
            title: 'Modèles',
            contentPlaceholder: '<h1>Contrat de prestation</h1>\n<p>Entre {{nom_client}} et ...</p>',
            previewSuffix: '(aperçu)',
            newTemplate: 'Nouveau modèle',
            uploadFile: 'Déposer un fichier',
            searchPlaceholder: 'Rechercher un modèle',
            colName: 'Nom', colSource: 'Source', colUsage: 'Utilisations',
            colUpdated: 'Mise à jour',
            sourceUpload: 'Déposé', sourceBlank: 'Éditeur',
            statusDraft: 'Brouillon', statusActive: 'Actif', statusArchived: 'Archivé',
            loadingList: 'Chargement des modèles…',
            emptyTitle: 'Aucun modèle pour le moment.',
            emptyAction: 'Créez votre premier modèle',
            countSuffix: 'modèles',
            editTitle: 'Modifier le modèle', createTitle: 'Nouveau modèle',
            fieldsButton: 'Placer les champs',
            originalFile: 'Télécharger le fichier d\'origine',
            previewFile: 'Aperçu PDF',
            nameLabel: 'Nom du modèle',
            namePlaceholder: 'ex. Contrat de prestation',
            nameError: 'Le nom du modèle doit contenir au moins 3 caractères.',
            categoryPlaceholder: 'Contrat',
            statusHelp: 'Un modèle actif nécessite au moins un champ de signature.',
            descriptionPlaceholder: 'Quand ce modèle est-il utilisé ?',
            rolesTitle: 'Parties signataires',
            addRole: 'Ajouter une partie',
            rolePlaceholder: 'Nom de la partie (Client)',
            roleKeyPlaceholder: 'clé',
            roleError: 'Chaque partie doit avoir un nom et une clé.',
            roleKeyError: 'Les clés des parties doivent être uniques.',
            actionSign: 'Signe', actionApprove: 'Approuve',
            actionFill: 'Remplit', actionCc: 'Reçoit une copie',
            contentLabel: 'Contenu',
            contentHelp: 'Utilisez des doubles accolades pour placer les champs : {sample}',
            contentError: 'Le contenu ne peut pas être vide.',
            usedWarning: 'Ce modèle a été utilisé {count} fois. L\'enregistrement crée la '
                + 'version v{version} ; les documents signés restent liés à la précédente.',
            uploadTitle: 'Déposer un fichier modèle',
            uploadPick: 'Choisir un PDF ou DOCX',
            uploadLimit: 'Jusqu\'à 20 Mo.',
            uploadNamePlaceholder: 'Laissez vide pour utiliser le nom du fichier',
            uploadNoFile: 'Choisissez d\'abord un fichier.',
            uploadTooBig: 'Le fichier dépasse la limite de 20 Mo.',
            deleteTitle: 'Supprimer le modèle',
            deleteBody: '{name} sera retiré de la liste.',
            deleteUsedNote: '{count} documents ont été signés avec ce modèle. '
                + 'L\'enregistrement est archivé, pas supprimé — les documents signés '
                + 'restent accessibles.'
        },

        nav: {
            sectionWork: 'Espace de travail', sectionAccount: 'Compte',
            home: 'Accueil', templates: 'Modeles', agreements: 'Contrats',
            verify: 'Verification', profile: 'Profil', organization: 'Organisation',
            logout: 'Deconnexion', newEnvelope: 'Nouvel envoi',
            homeSubtitle: 'Decouvrez le deroulement de la signature, etape par etape.',
            templatesSubtitle: 'Conservez en modele les documents que vous envoyez souvent.',
            agreementsSubtitle: 'Suivez vos envois et l\'etat de chaque signature.',
            verifySubtitle: 'Verifiez qu\'un document signe n\'a pas ete modifie.',
            profileSubtitle: 'Gerez vos informations et votre signature par defaut.',
            organizationSubtitle: 'Informations de l\'organisation, membres et invitations.',
            newEnvelopeSubtitle: 'Choisissez le document, ajoutez les destinataires, envoyez.',
            rateLimitTitle: 'Limite de requetes atteinte', backToList: 'Retour a la liste'
        },
        sign: {
            signerLabel: 'Signataire',
            tabUpload: 'Importer', tabPad: 'Tablette de signature',
            tabPadMock: 'Tablette de signature (mock)',
            biometricNotice: 'Pendant la signature sur la tablette, la pression et le rythme du stylo sont aussi enregistres. Ces donnees sont chiffrees et ne peuvent etre ouvertes que pour un examen autorise en cas de contestation.',

            uploadPick: 'Choisissez une image de signature',
            uploadHint: 'PNG ou JPG. Un fond blanc est rendu transparent automatiquement.',
            uploadBadType: 'Seules les images PNG ou JPG sont acceptees.',
            uploadTooBig: 'L\'image depasse la limite de 5 Mo.',
            padConnecting: 'Recherche de la tablette…',
            padReady: 'Tablette prete. Signez sur la tablette.',
            padClear: 'Effacer la tablette',
            padEmpty: 'Aucune signature sur la tablette. Signez d\'abord.',
            padNoScript: 'La prise en charge de la tablette n\'est pas installee sur ce serveur (SigWebTablet.js absent).',
            padNoService: 'Le service de la tablette (SigWeb) ne fonctionne pas sur cet ordinateur. Est-il installe et la tablette connectee ?',
            padRetry: 'Reessayer',

            guideStart: 'Commencer', guideNext: 'Suivant', guideFinish: 'Terminer',
            guideFill: 'Remplir',
            dragHint: 'Faites glisser pour ajuster, cliquez pour changer',
            signHere: 'Signer', initialHere: 'Parapher',
            adoptTitle: 'Creez votre signature',
            adoptLead: 'Cette signature sera appliquee a tous les champs de signature du document.',
            adoptButton: 'Adopter et signer',
            fieldsLeft: '{count} champs restants',
            allDone: 'Tous les champs sont remplis',
            finishNeedsConsent: 'Cochez la case de consentement avant d\'envoyer.',
            finishNeedsFields: 'Remplissez d\'abord tous les champs obligatoires.',
            changeSignature: 'Changer la signature',

            awaitingTitle: 'Votre signature est demandee',
            sentBy: 'Envoye par',
            documentTitle: 'Document',
            openInNewTab: 'Ouvrir dans un nouvel onglet',
            consentTitle: 'Consentement',
            secureNote: 'Ce lien vous est personnel et ne peut servir qu\'une fois.',
            doneTitle: 'Termine',
            footerNote: 'Ce document a ete signe avec {brand}.',
            footerLink: 'Decouvrir le produit',

            docUnavailable: 'Le document ne peut pas etre affiche',
            fieldsTitle: 'Champs a remplir',
            checkboxLabel: 'J\'accepte',
            selectOption: 'Choisir',
            signatureTitle: 'Votre signature',
            tabSaved: 'Ma signature enregistree', tabDraw: 'Tracer', tabType: 'Saisir',
            savedHint: 'La signature enregistree dans votre profil sera utilisee.',
            drawHint: 'Signez avec la souris ou le doigt.',
            typePlaceholder: 'Saisissez votre nom',
            clear: 'Effacer',
            consent: 'J\'accepte de signer ce document par voie electronique et que ma signature '
                + 'electronique produise les memes effets juridiques qu\'une signature manuscrite.',
            submit: 'Signer et envoyer',
            decline: 'Refuser de signer',
            declineTitle: 'Refuser de signer',
            declineLead: 'Cette action est irreversible. L\'expediteur sera informe de votre refus.',
            declineReason: 'Motif',
            declineReasonPlaceholder: 'Indiquez brievement pourquoi vous ne signez pas',
            declineSubmit: 'Refuser',
            blockedHint: 'Ce lien n\'est plus utilisable. Contactez la personne qui a envoye le document.',
            doneHint: 'Vous pouvez fermer cet onglet.',
            missingToken: 'Le lien de signature est absent ou incorrect.',
            errRequired: 'Champs obligatoires : {fields}',
            errSignature: 'Tracez votre signature ou creez-en une en la saisissant.'
        },
        join: {
            loading: 'Verification de l\'invitation…',
            title: 'Rejoignez l\'equipe',
            lead: 'Vous etes invite a rejoindre {org}.',
            signInFirst: 'Connectez-vous pour accepter l\'invitation. Si vous n\'avez pas encore '
                + 'de compte, vous pouvez en creer un depuis l\'ecran de connexion.',
            signIn: 'Se connecter ou creer un compte',
            signingIn: 'Connexion en attente…',
            accept: 'Accepter l\'invitation',
            oneOrgOnly: 'Un utilisateur ne peut appartenir qu\'a une seule organisation.',
            blockedHint: 'Cette invitation n\'est plus utilisable. Contactez la personne qui vous a invite.',
            joinedHint: 'Vous avez desormais acces aux modeles et aux dossiers de signature de l\'organisation.',
            goToApp: 'Aller a l\'application',
            missingToken: 'Le lien d\'invitation est absent ou incorrect.'
        },
        verify: {
            title: 'Verifier un document',
            expandAll: 'Tout deplier', collapseAll: 'Tout replier',
            rowAppended: 'Ajoute apres la signature',
            appendedValidation: 'donnees de validation uniquement ({bytes} octets)',
            appendedNone: 'rien',
            appendedContent: 'le contenu a ete modifie ({bytes} octets)',
            appendedHint: 'La norme PDF autorise l\'ajout de donnees de validation (DSS/LTV) '
                + 'apres la signature, et Adobe les considere comme valides. Tant que la partie '
                + 'ajoutee ne touche pas au contenu des pages, la signature n\'est pas rompue.',

            tsAuthority: 'Horodatage emis par',
            tsAuthorityIssuer: 'Son certificat emis par',
            tsSerial: 'Numero de serie de l\'horodatage',
            tsMatches: 'L\'horodatage appartient a cette signature',
            tsMatchesHint: 'L\'empreinte enregistree par l\'autorite correspond a celle de la '
                + 'signature du fichier. Sinon, l\'horodatage aurait ete copie d\'un autre document.',
            tsNoAuthority: 'Le jeton ne contient pas le certificat de l\'autorite',

            sectionChain: 'Chaine de certificats',
            chainSigner: 'Signataire', chainIntermediate: 'Intermediaire', chainRoot: 'Racine',
            chainIssuer: 'Emis par', chainSerial: 'Numero de serie', chainValidity: 'Validite',
            chainRevocation: 'Verification de revocation', chainNoRevocation: 'aucune adresse',
            chainSelfSigned: 'Auto-signe',
            chainHint: 'La chaine va du signataire jusqu\'a la racine. Chaque certificat est '
                + 'emis par celui du dessus ; la racine se signe elle-meme.',

            sectionLtv: 'Validation a long terme (LTV)',
            ltvLevel: 'Niveau', ltvNone: 'Aucun',
            ltvRevocationEmbedded: 'Donnees de revocation integrees',
            ltvDocTimestamp: 'Horodatage du document',
            ltvYes: 'oui', ltvNo: 'non',
            ltvGoodText: 'Les donnees de revocation sont dans le document. Cette signature reste '
                + 'verifiable meme apres l\'expiration du certificat ou la fermeture de l\'autorite.',
            ltvNoneText: 'Aucune donnee de revocation n\'est integree. A l\'expiration du '
                + 'certificat, le verificateur doit joindre l\'autorite de certification ; '
                + 'sans reponse, la signature apparait comme « non verifiable ».',
            ltvLtText: 'Les donnees de revocation sont integrees mais il manque un horodatage '
                + 'du document. L\'ajouter scelle aussi le moment ou ces donnees etaient valides.',

            sectionAdobe: 'Comment le verifier dans Adobe Reader',
            adobeLead: 'Sur votre propre ordinateur, sans nous solliciter :',
            adobeStep1: 'Ouvrez le document dans Adobe Acrobat Reader (le lecteur PDF du navigateur n\'affiche pas la signature).',
            adobeStep2: 'Ouvrez le panneau de signature a gauche : bouton « Panneau de signature », ou Affichage → Afficher/Masquer → Volets de navigation → Signatures.',
            adobeStep3: 'Cliquez sur la signature. S\'il indique que le document n\'a pas ete modifie depuis la signature, le contenu est intact.',
            adobeStep4: 'Cliquez sur Proprietes de la signature, puis « Afficher le certificat du signataire » pour voir la chaine.',
            adobeStep5: 'L\'onglet Confiance de la fenetre du certificat indique si l\'identite du signataire a ete verifiee.',
            adobeStep6: 'S\'il y a un horodatage, il apparait sur sa propre ligne dans les proprietes, avec le nom de l\'autorite emettrice.',
            adobeNote: 'Un triangle jaune ne signifie pas que le document est corrompu : il '
                + 'indique que l\'identite du signataire n\'a pas pu etre confirmee par une '
                + 'autorite independante. La modification du contenu figure sur une autre ligne.',

            fileHint: 'Jusqu\'a 20 Mo.',
            codePlaceholder: 'ex. A7K2-9MPX-3RTQ',
            rowStatus: 'Statut',
            footer: 'DocuLock',

            subtitle: 'Verifiez qu\'un document signe l\'a bien ete et qu\'il n\'a pas ete modifie.',
            tabCode: 'Par code de verification', tabFile: 'En deposant le fichier',
            codeLead: 'Saisissez le code imprime sur la derniere page du document signe.',
            codeHint: 'Vous pouvez saisir le code court de verification ou l\'identifiant du document (UUID).',
            fileLead: 'Deposez le PDF. Le fichier est seulement verifie, jamais conserve.',
            dropHere: 'Deposez le PDF ici, ou cliquez pour choisir',
            check: 'Verifier',
            sectionSignature: 'Signature numerique',
            rowSigned: 'Le document est signe numeriquement',
            rowUnmodified: 'Non modifie apres la signature',
            rowUnmodifiedHint: 'Un seul caractere modifie ferait passer cette ligne a « Non ».',
            rowCovers: 'La signature couvre tout le document',
            rowCertValid: 'Certificat dans sa periode de validite',
            rowSigner: 'Scelle par',
            rowCertPeriod: 'Validite du certificat',
            selfSignedTitle: 'Le signataire du sceau n\'a pas pu etre verifie.',
            selfSignedText: 'Le document n\'a pas ete modifie, nous l\'avons verifie. En revanche, '
                + 'l\'identite de l\'organisation qui l\'a scelle n\'est pas confirmee par une '
                + 'autorite de certification independante. Adobe Reader affichera « identite du '
                + 'signataire inconnue ».',
            sectionTimestamp: 'Horodatage',
            timestampHint: 'Cette heure provient d\'une autorite d\'horodatage independante, '
                + 'et non de l\'horloge du systeme signataire.',
            noTimestamp: 'Ce document ne porte pas d\'horodatage. L\'heure de signature repose '
                + 'uniquement sur l\'horloge du systeme signataire.',
            sectionRecord: 'Enregistrement',
            rowSubject: 'Objet', rowDocument: 'Document',
            rowCompleted: 'Finalise le', rowCode: 'Code de verification',
            rowDocumentId: 'Identifiant du document',
            rowChain: 'Chaine d\'evenements intacte',
            rowChainHint: 'Chaque entree porte l\'empreinte de la precedente ; en retirer une la romprait.',
            sectionSigners: 'Signataires',
            sectionMatch: 'Correspondance avec nos registres',
            matchYes: 'Ce fichier est identique, octet pour octet, au document enregistre.',
            matchNo: 'Ce fichier est introuvable dans nos registres. Il peut provenir d\'un autre '
                + 'systeme ou avoir ete re-enregistre apres sa production.',
            rowChecksum: 'Empreinte du fichier (SHA-256) :',
            sectionSelf: 'Verifier sans nous solliciter',
            selfText: 'Ouvrez le document dans Adobe Acrobat Reader. Le panneau de signature '
                + 'indique s\'il a ete modifie apres la signature, sans jamais contacter notre '
                + 'systeme. La signature numerique est integree au fichier.',
            notFound: 'Aucun document ne correspond a ce code.',
            emptyCode: 'Saisissez le code de verification.',
            noFile: 'Choisissez d\'abord un fichier PDF.',
            notPdf: 'Seuls les fichiers PDF peuvent etre verifies.',
            networkError: 'Serveur injoignable. Verifiez votre connexion.',
            serverError: 'Une erreur est survenue lors de la verification.',
            verdictValid: 'Document verifie',
            verdictValidText: 'La signature est valide et le document n\'a pas ete modifie depuis.',
            verdictBroken: 'Document modifie',
            verdictBrokenText: 'La signature ne correspond pas. Ce fichier a ete modifie apres signature.',
            verdictUnsigned: 'Document non signe',
            verdictUnsignedText: 'Aucune signature numerique n\'a ete trouvee dans ce PDF.',
            verdictRecord: 'Enregistrement trouve',
            verdictRecordText: 'Le document figure dans nos registres. Pour verifier le fichier '
                + 'lui-meme, utilisez l\'onglet « En deposant le fichier ».'
        },
        intro: {
            title: 'Comment ça marche',
            subtitle: 'Sept étapes, du document à la signature puis à la vérification.',
            stepWord: 'Étape', prev: 'Retour', next: 'Suivant', restart: 'Recommencer',
            footnote: 'Cliquez sur une étape ci-dessus pour y accéder directement.',
            steps: [
                { short: 'Document', title: 'Préparez le document',
                  text: 'Partez de ce que vous devez faire signer. Déposez un PDF si vous en avez '
                      + 'un, ou rédigez-le dans l\'éditeur. Conservez en modèle ce que vous envoyez souvent.',
                  points: ['Déposez un PDF ou DOCX', 'Ou rédigez-le ici', 'Choisissez parmi vos modèles'] },
                { short: 'Champs', title: 'Placez les champs',
                  text: 'Faites glisser les champs de signature, de paraphe, de date et de texte '
                      + 'sur le document. Chaque champ appartient à une partie, qui ne voit que les siens.',
                  points: ['Glisser-déposer', 'Chaque champ a un propriétaire', 'Positions en proportions'] },
                { short: 'Destinataires', title: 'Ajoutez les destinataires',
                  text: 'Saisissez le nom et l\'e-mail de chaque signataire. À plusieurs, ils '
                      + 'signent dans l\'ordre : la deuxième invitation attend la première signature.',
                  points: ['Nom et e-mail suffisent', 'Aucun compte requis', 'Signature séquentielle possible'] },
                { short: 'Envoi', title: 'Envoyez',
                  text: 'Chaque destinataire reçoit un e-mail avec son propre lien. Le lien est à '
                      + 'usage unique et cesse de fonctionner à son expiration.',
                  points: ['Un lien personnel par personne', 'À usage unique', 'Relances possibles'] },
                { short: 'Signature', title: 'Ils signent',
                  text: 'Le destinataire ouvre le lien, lit le document dans son navigateur, '
                      + 'remplit les champs et trace sa signature. Rien n\'est enregistré sans la '
                      + 'case de consentement.',
                  points: ['Rien à installer', 'Signature tracée ou saisie', 'Le consentement est recueilli'] },
                { short: 'Sceau', title: 'Le document est scellé',
                  text: 'À la dernière signature, le PDF est produit, un récapitulatif y est ajouté '
                      + 'et le fichier est scellé par un certificat. L\'heure provient d\'une '
                      + 'autorité indépendante.',
                  points: ['Signature cryptographique intégrée', 'Horodatage appliqué', 'Chaîne d\'événements inscrite'] },
                { short: 'Vérification', title: 'Vérifiable à tout moment',
                  text: 'Quiconque détient le document peut le contrôler — avec le code qui y '
                      + 'figure, en déposant le fichier, ou dans Adobe Reader sans nous solliciter.',
                  points: ['Par code de vérification', 'En déposant le fichier', 'Dans Adobe Reader, en toute indépendance'] }
            ]
        },
        landing: {
            navHow: 'Comment ça marche', navVerify: 'Vérifier un document', login: 'Connexion',
            heroTitle: 'Faites signer vos documents par voie électronique',
            heroSubtitle: 'Déposez le contrat, ajoutez les personnes qui doivent signer, et '
                + 'laissez-nous le reste. Chaque étape est enregistrée et le document finalisé '
                + 'est scellé numériquement.',
            heroPrimary: 'Commencer', heroSecondary: 'Vérifier un document',
            featuresTitle: 'Ce que fait la plateforme',
            featuresLead: 'Sans faire venir personne au bureau pour une signature manuscrite.',
            features: [
                { icon: 'fa-file-text-o', title: 'Modèle ou fichier',
                  text: 'Déposez un PDF, rédigez-en un dans l\'éditeur ou partez d\'un modèle '
                      + 'enregistré. Conservez les documents que vous envoyez souvent.' },
                { icon: 'fa-crosshairs', title: 'Placez les champs vous-même',
                  text: 'Faites glisser les champs de signature, de paraphe, de date et de texte '
                      + 'sur le document. Chaque champ appartient à une partie.' },
                { icon: 'fa-envelope-o', title: 'Envoi par e-mail',
                  text: 'Les signataires reçoivent un lien personnel. Aucun compte requis. '
                      + 'S\'ils sont plusieurs, ils signent dans l\'ordre.' },
                { icon: 'fa-list-alt', title: 'Piste d\'audit chaînée',
                  text: 'Création, consultation, signature et finalisation sont enregistrées avec '
                      + 'l\'heure, l\'adresse IP et le navigateur. Chaque entrée porte l\'empreinte '
                      + 'de la précédente.' },
                { icon: 'fa-lock', title: 'Sceau numérique',
                  text: 'Le document finalisé est signé avec un certificat. Un seul octet modifié '
                      + 'ensuite invalide la signature — visible aussi dans Adobe Reader.' },
                { icon: 'fa-clock-o', title: 'Horodatage',
                  text: 'L\'heure de signature provient d\'une autorité d\'horodatage indépendante, '
                      + 'pas de notre propre horloge.' }
            ],
            howTitle: 'Quatre étapes',
            steps: [
                { title: 'Préparez le document',
                  text: 'Déposez un fichier ou choisissez un modèle, puis placez les champs de signature.' },
                { title: 'Ajoutez les destinataires',
                  text: 'Le nom et l\'e-mail suffisent. Définissez l\'ordre si la signature doit être '
                      + 'séquentielle.' },
                { title: 'Envoyez',
                  text: 'Chacun reçoit son propre lien et signe depuis son navigateur.' },
                { title: 'Récupérez le document signé',
                  text: 'Le PDF scellé et le récapitulatif sont prêts dès la dernière signature.' }
            ],
            verifyTitle: 'Vérifiez un document reçu',
            verifyLead: 'Saisissez le code de vérification imprimé sur la dernière page du document '
                + 'signé. Aucun compte nécessaire.',
            verifyPlaceholder: 'ex. A7K2-9MPX-3RTQ',
            verifyButton: 'Vérifier',
            verifyHint: 'Vous pouvez aussi ouvrir le fichier dans Adobe Reader et contrôler la '
                + 'signature sans nous solliciter.',
            chainOk: 'Chaîne d\'enregistrements valide', chainBroken: 'Chaîne d\'enregistrements rompue',
            signedYes: 'Scellé numériquement', signedNo: 'Non scellé',
            timestampLabel: 'Horodatage',
            notFound: 'Aucun document ne correspond à ce code.',
            emptyCode: 'Saisissez le code de vérification.',
            networkError: 'Serveur injoignable. Vérifiez votre connexion.',
            serverError: 'Une erreur est survenue lors de la vérification.',
            footerCredit: 'Conçu et développé par l\'équipe DocuLock.',
            footerRights: '2024 © TOUS DROITS RÉSERVÉS'
        },
        envelopes: {
            title: 'Dossiers de signature',
            deleteAction: 'Supprimer',
            selectAll: 'Tout selectionner sur cette page',
            selectedCount: '{count} selectionnes',
            clearSelection: 'Effacer la selection',
            deleteSelected: 'Supprimer la selection',
            deleteTitle: 'Supprimer {count} dossiers de signature ?',
            deleteLead: 'Les dossiers supprimes disparaissent de la liste. Cette action est irreversible.',
            deleteMore: 'et {count} de plus',
            deleteActiveWarn: '{count} dossiers attendent encore des signatures. Ils seront annules et leurs liens de signature fermes.',
            deleteKeepNote: 'Les documents finalises sont conserves comme preuve ; le PDF signe detenu par les parties reste verifiable.',
            deleteConfirm: 'Oui, supprimer',

            showing: '{from}-{to} sur {total}',
            perPage: 'Par page',
            first: 'Premiere page', last: 'Derniere page',

            searchPlaceholder: 'Rechercher par objet, nom ou e-mail',
            viewCards: 'Cartes', viewList: 'Liste',
            noMatch: 'Aucun resultat pour votre recherche.',
            colSigners: 'Signataires', moreSigners: '+{count}',

            colSubject: 'Objet', colRecipients: 'Destinataires', colUpdated: 'Mise a jour',
            signCount: '{signed} / {total} signes',
            statusDraft: 'Brouillon', statusSent: 'Envoye', statusProgress: 'Signature en cours',
            statusCompleted: 'Termine', statusDeclined: 'Refuse', statusVoided: 'Annule',
            rcpPending: 'En attente', rcpSent: 'Envoye', rcpViewed: 'Consulte',
            rcpSigned: 'Signe', rcpDeclined: 'Refuse',
            loadingList: 'Chargement des dossiers…',
            empty: 'Aucun dossier de signature pour le moment.',
            countSuffix: 'dossiers',
            detail: 'Details', send: 'Envoyer', remind: 'Relancer',
            downloadOriginal: 'Telecharger l\'original', downloadSigned: 'PDF signe',
            voidAction: 'Annuler',
            auditValid: 'Piste d\'audit : valide', auditBroken: 'Piste d\'audit : ROMPUE',
            recipientsTitle: 'Destinataires', eventsTitle: 'Chaine d\'evenements',
            linksTitle: 'Liens de signature',
            mailSent: 'e-mail envoye', mailFailed: 'e-mail non envoye',
            declineReason: 'Refus', sentAt: 'Envoi',
            voidTitle: 'Annuler le dossier',
            voidBody: '{subject} sera annule. Les liens de signature ouverts cessent de fonctionner et '
                + 'cette action est irreversible.',
            voidReason: 'Motif',
            evCreated: 'Cree', evSent: 'Lien emis', evMailed: 'E-mail envoye',
            evViewed: 'Consulte', evSigned: 'Signe', evCompleted: 'Termine',
            evDeclined: 'Refuse', evVoided: 'Annule', evRebuilt: 'Sortie regeneree',
            signedSuffix: '(signe)'
        },
        newEnvelope: {
            title: 'Nouveau dossier de signature',
            stepDocument: 'Document', stepDetails: 'Details', stepRecipients: 'Destinataires',
            tabTemplateHint: 'Partez d\'un de vos modeles',
            tabUploadHint: 'Deposez un fichier PDF ou DOCX',
            tabBlankHint: 'Redigez le texte ici',
            order: 'Ordre',

            sent: 'Envoye',
            newPackage: 'Creer un autre dossier',
            document: 'Document',
            tabTemplate: 'Depuis un modele', tabUpload: 'Deposer un fichier', tabBlank: 'Rediger',
            pickTemplate: 'Choisir un modele',
            groupActive: 'Actifs', groupUnusable: 'Inutilisables',
            noTemplates: 'Aucun modele. Creez-en un depuis l\'ecran Modeles, ou deposez un fichier ci-dessus.',
            noneActive: 'Il y a {count} modeles mais aucun n\'est actif. Pour utiliser un modele, ajoutez '
                + 'au moins un champ de signature depuis l\'ecran Modeles et passez son statut a « Actif ».',
            noDescription: 'Pas de description',
            parties: 'Parties',
            uploadHelp: 'Jusqu\'a 20 Mo. Le cadre de signature est place en bas de la derniere page.',
            saveAsTemplate: 'Enregistrer aussi ce document comme modele',
            templateNamePlaceholder: 'Nom du modele (vide = objet du dossier)',
            subject: 'Objet',
            subjectPlaceholder: 'Le titre que le destinataire verra dans l\'e-mail',
            message: 'Message',
            messagePlaceholder: 'Note facultative',
            recipients: 'Destinataires',
            addRecipient: 'Ajouter un destinataire',
            lockedRoles: 'Le modele definit les parties. Indiquez une personne pour chacune.',
            orderNote: 'Avec plusieurs destinataires, la signature est sequentielle : le deuxieme lien '
                + 'attend la premiere signature.',
            createAndSend: 'Creer et envoyer',
            saveDraft: 'Enregistrer comme brouillon',
            draftSaved: 'Brouillon enregistre. Poursuivez depuis l\'ecran des dossiers pour l\'envoyer. '
                + 'Identifiant : {id}',
            linkNote: 'Si l\'e-mail n\'a pas pu etre envoye, vous pouvez transmettre ce lien vous-meme :',
            errSubject: 'L\'objet est obligatoire.',
            errTemplate: 'Choisissez un modele.',
            errFile: 'Choisissez un fichier.',
            errContent: 'Le contenu du document est vide.',
            errRecipient: 'Chaque destinataire doit avoir un nom et un e-mail valide.',
            errDuplicate: 'Le meme e-mail a ete ajoute deux fois.'
        },
        editor: {
            addField: 'Ajouter un champ',
            zoom: 'Zoom', fitWidth: 'Ajuster',
            selectHint: 'Faites glisser un champ pour le deplacer, redimensionnez par le coin.',
            noFields: 'Aucun champ. Choisissez un type et une partie a gauche, puis cliquez sur le document.',
            deleteField: 'Supprimer le champ',
            pageOf: 'Page {page} sur {total}',

            addHelp: 'Choisissez un type et une partie, puis cliquez sur le document.',
            fieldType: 'Type de champ', party: 'Partie',
            noRoles: 'Aucune partie definie sur ce modele. Ajoutez-en une depuis l\'ecran Modeles.',
            ready: 'Pret a activer : {count} champs de signature.',
            needSignature: 'Au moins un champ de signature est necessaire pour activer.',
            fields: 'Champs',
            label: 'Libelle', requiredField: 'Obligatoire',
            preparing: 'Preparation du document…',
            page: 'Page',
            cannotShow: 'Le document n\'a pas pu etre affiche. Vous pouvez ajouter des champs, mais sans '
                + 'voir ou ils tombent.',
            saveActivate: 'Enregistrer et activer',
            toDraft: 'Repasser en brouillon',
            errLabel: 'Un champ n\'a pas de libelle.',
            errFirstRole: 'Ajoutez d\'abord une partie au modele.',
            tSignature: 'Signature', tInitials: 'Paraphe', tText: 'Texte', tDate: 'Date',
            tNumber: 'Nombre', tEmail: 'E-mail', tCheckbox: 'Case a cocher',
            tDropdown: 'Liste', tTextarea: 'Texte long', tStamp: 'Cachet'
        },
        profile: {
            title: 'Profil',
            account: 'Informations du compte',
            fullName: 'Nom complet', jobTitle: 'Fonction', phone: 'Telephone',
            organization: 'Organisation', role: 'Role', locale: 'Langue / fuseau horaire',
            lastLogin: 'Derniere connexion', timezone: 'Fuseau horaire', country: 'Pays',
            verified: 'verifie', notVerified: 'non verifie',
            titleHint: 'Apparait dans le bloc de signature',
            lockedHint: 'L\'e-mail, le role et l\'organisation ne se modifient pas ici.',
            errName: 'Le nom complet doit contenir au moins 2 caracteres.',
            signatureTitle: 'Votre signature par defaut',
            signatureLead: 'Enregistrez-la une fois et elle sera prete a chaque signature, sans avoir a '
                + 'la retracer.',
            change: 'Modifier',
            tabDraw: 'Tracer', tabType: 'Saisir',
            drawHint: 'Signez avec la souris ou le doigt.',
            typePlaceholder: 'Saisissez votre nom',
            saveSignature: 'Enregistrer la signature', clearSignature: 'Effacer',
            errSignature: 'Tracez votre signature ou saisissez votre nom d\'abord.',
            roleOwner: 'Proprietaire', roleAdmin: 'Administrateur', roleMember: 'Membre',
            roleViewer: 'Lecteur',
            stActive: 'Actif', stInvited: 'Invite', stPassive: 'Inactif', stSuspended: 'Suspendu'
        },
        organization: {
            memberCount: '{count} membres',
            copyKey: 'Copier la cle',
            copyId: 'Copier l\'identifiant',
            apiKeyVisible: 'Les cles sont visibles par tous les membres de l\'organisation.',

            alreadyMember: '{email} est deja membre de votre organisation.',
            alreadyInvited: 'Une invitation est deja en attente pour {email}.',

            apiTitle: 'Cles API',
            apiLead: 'Utilisees par les systemes externes pour creer des dossiers.',
            apiNew: 'Nouvelle cle',
            apiName: 'Nom de la cle',
            apiNamePlaceholder: 'ex. Integration CRM',
            apiValidity: 'Validite',
            apiMonths: '{count} mois',
            apiNone: 'Aucune cle API pour le moment.',
            apiCreated: 'Cle creee',
            apiOnceWarning: 'Cette cle ne sera plus affichee. Copiez-la maintenant et conservez-la en lieu sur.',
            apiAccountId: 'Identifiant du compte',
            apiUsage: 'Appels', apiLastUsed: 'Derniere utilisation', apiNever: 'jamais',
            apiRevoke: 'Revoquer',
            apiRevoked: 'Revoquee',
            apiExpired: 'Expiree',
            apiActive: 'Active',
            apiErrName: 'Le nom de la cle est obligatoire.',
            apiEndpoint: 'Point de terminaison',

            invite: 'Inviter quelqu\'un',
            info: 'Informations de l\'organisation',
            name: 'Nom de l\'organisation', legalName: 'Raison sociale', taxNumber: 'Numero fiscal',
            phone: 'Telephone', address: 'Adresse', locale: 'Langue / fuseau horaire',
            slugLocked: 'Le nom court ({slug}) ne peut pas etre modifie — il est utilise dans les liens.',
            members: 'Membres', person: 'Personne',
            invitations: 'Invitations', noInvitations: 'Aucune invitation en attente.',
            inviteLanguage: 'Langue', validity: 'Valable jusqu\'au',
            inviteTitle: 'Inviter quelqu\'un',
            inviteLead: 'La personne invitee rejoint l\'organisation via le lien recu par e-mail.',
            copyLink: 'Copier le lien',
            copied: 'Copie',
            inviteSentTitle: 'Invitation prete',

            inviteName: 'Nom complet', inviteEmail: 'E-mail',
            inviteLangHelp: 'L\'e-mail d\'invitation est envoye dans cette langue.',
            inviteRole: 'Role',
            inviteCreated: 'Invitation creee pour {name}.',
            inviteLinkHint: 'Si l\'e-mail n\'a pas pu etre envoye, transmettez ce lien vous-meme :',
            sendInvite: 'Envoyer l\'invitation',
            mailNotSent: 'e-mail non envoye',
            invPending: 'En attente', invAccepted: 'Acceptee',
            invRevoked: 'Annulee', invExpired: 'Expiree',
            errName: 'Le nom de l\'organisation est obligatoire.',
            errInviteName: 'Le nom est obligatoire.',
            errInviteEmail: 'Saisissez une adresse e-mail valide.',
            notInOrg: 'Vous n\'appartenez pas encore a une organisation.'
        },
        orgSetup: {
            title: 'Créez votre organisation',
            lead: 'Vos modèles, vos dossiers de signature et vos collègues appartiennent à '
                + 'une organisation. Créez la vôtre pour continuer.',
            nameLabel: 'Nom de l\'organisation',
            namePlaceholder: 'ex. DocuLock Software Ltd.',
            nameError: 'Le nom de l\'organisation est obligatoire.',
            slugPreview: 'Nom court',
            taxNumber: 'Numéro fiscal',
            phone: 'Téléphone',
            address: 'Adresse',
            help: 'Seul le nom de l\'organisation est obligatoire. Vous pourrez compléter '
                + 'le reste depuis les paramètres.',
            submit: 'Créer et continuer'
        }
    }
};


// WEBPACK FOOTER //
// ./src/config/i18n.js